/* mrd-ui.js — i comportamenti delle sezioni MRD, in un file solo caricato dal layout.
 *
 * Perche' nel layout e non dentro le sezioni: nell'editor una sezione aggiunta a una pagina
 * arriva senza far girare i suoi <script>. Caricato una volta per tutte, il file ascolta
 * `shopify:section:load` e aggancia da solo quello che arriva.
 *
 * Contratto ad attributi dati (nessuna sezione scrive JavaScript suo):
 *   [data-mrd-carousel]  fila che scorre: [data-mrd-track] (ogni figlio e' una slide),
 *                        [data-mrd-prev] [data-mrd-next] [data-mrd-dot]; opzioni
 *                        data-mrd-center (slide attiva al centro), data-mrd-start="N" e
 *                        data-mrd-autoplay="secondi" (avanza da sola quando e' a schermo)
 *   [data-mrd-fade]      elementi sovrapposti che si alternano: [data-mrd-fade-item],
 *                        [data-mrd-dot]; data-mrd-autoplay="secondi", data-mrd-swipe
 *   [data-mrd-marquee]   nastro infinito: [data-mrd-marquee-track] con due meta' identiche,
 *                        data-mrd-speed="pixel al secondo"
 *   [data-mrd-videos]    video che partono muti quando sono in vista: video[data-mrd-video]
 *                        dentro [data-mrd-video-frame] con il tasto [data-mrd-sound]
 *   [data-mrd-compare]   prima/dopo: [data-mrd-compare-area], [data-mrd-compare-handle],
 *                        data-mrd-start="percentuale"
 *   [data-mrd-header]    menu a scomparsa: [data-mrd-menu-open], [data-mrd-menu],
 *                        [data-mrd-menu-panel], [data-mrd-header-bar]
 *   [data-mrd-product]   scheda prodotto: radio in [data-mrd-option], varianti in
 *                        script[data-mrd-variants], modulo [data-mrd-product-form]
 *   data-mrd-thumbs      su una fila che scorre: i [data-mrd-dot] sono miniature da tenere in vista
 *   [data-mrd-sticky-bar] barra d'acquisto fissa: guarda [data-mrd-atc-main] e [data-mrd-atc-watch]
 *   [data-mrd-cart]      carrello a tendina: [data-mrd-cart-content] (ridisegnato da Shopify),
 *                        [data-mrd-cart-open] [data-mrd-cart-close] [data-mrd-cart-qty]
 *                        [data-mrd-cart-count]; intercetta i moduli verso /cart/add
 */
(function () {
  'use strict';

  if (window.MrdUI) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* LE LINGUE (10/10/2026: arriva l'inglese). Le scritte di questo file le scrive il layout in
     window.mrdTesti dal file di lingua del tema (locales/*.json, chiavi mrd.js.*); qui resta
     l'italiano di riserva. RADICE e' l'inizio degli indirizzi nella lingua e nel mercato in cui
     si e': «/» in italiano, «/en/», «/en-eu/», «/it-eu/». Ogni indirizzo scritto qui dentro parte
     da li', se no da una pagina inglese si tornerebbe all'italiano. */
  var TESTI = window.mrdTesti || {};
  function testo(chiave, riserva) { return TESTI[chiave] || riserva; }
  var RADICE = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var LINGUA_DATE = (function () {
    var l = (document.documentElement.getAttribute('lang') || 'it').toLowerCase();
    if (l.indexOf('en') === 0) return 'en-GB';
    if (l.indexOf('it') === 0) return 'it-IT';
    return l;
  })();

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    return function () { target.removeEventListener(type, handler, options); };
  }

  /* ---------------------------------------------------------------------------
     Fila che scorre (slide immagine+testo, carosello video)
     Scorrimento nativo con scroll-snap: col dito si comporta come il telefono si aspetta,
     frecce e pallini chiedono solo di andare a una slide.
     --------------------------------------------------------------------------- */
  function createScroller(root) {
    var track = root.querySelector('[data-mrd-track]');
    if (!track) return null;
    /* Slide e pallini nascosti (hidden) non contano: nella scheda prodotto le foto degli altri
       colori restano nella pagina ma spente (07/10/2026). Senza questo la slide «piu' vicina»
       poteva risultare una nascosta, che a schermo misura zero. */
    function visibile(el) { return !el.hidden; }
    var slides = Array.prototype.slice.call(track.children).filter(visibile);
    if (!slides.length) return null;

    var prev = root.querySelector('[data-mrd-prev]');
    var next = root.querySelector('[data-mrd-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-dot]')).filter(visibile);
    var centerMode = root.hasAttribute('data-mrd-center');
    /* Miniature al posto dei pallini (galleria della scheda prodotto): quella attiva resta in
       vista dentro la sua fila, che puo' scorrere. */
    var thumbs = root.hasAttribute('data-mrd-thumbs');
    var revealed = -1;
    var index = clamp(parseInt(root.getAttribute('data-mrd-start') || '0', 10) || 0, 0, slides.length - 1);
    var frame = 0;
    var lastWidth = 0;
    var offs = [];
    /* Slide verso cui sta andando uno scorrimento morbido partito da una freccia. Serve ai
       clic ravvicinati: a meta' scorrimento la slide «piu' vicina» e' ancora quella di
       partenza, e senza questo il secondo clic ripartirebbe da li' (un passo perso). */
    var target = null;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function padStart() {
      return parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    }

    /* Le misure si prendono dai rettangoli a schermo, non da offsetLeft: offsetLeft e'
       relativo all'antenato posizionato e sbaglia appena la fila sta dentro un contenitore
       posizionato. Si usa il CENTRO della slide, che non cambia se l'interno e' rimpicciolito. */
    function targetFor(i) {
      var t = track.getBoundingClientRect();
      var s = slides[i].getBoundingClientRect();
      var left = centerMode
        ? track.scrollLeft + (s.left + s.width / 2) - (t.left + t.width / 2)
        : track.scrollLeft + (s.left - t.left) - padStart();
      return clamp(Math.round(left), 0, maxScroll());
    }

    function nearest() {
      var t = track.getBoundingClientRect();
      var ref = centerMode ? t.left + t.width / 2 : t.left + padStart();
      var best = 0;
      var bestDistance = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var r = slides[i].getBoundingClientRect();
        var point = centerMode ? r.left + r.width / 2 : r.left;
        var distance = Math.abs(point - ref);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    }

    function render() {
      frame = 0;
      var max = maxScroll();
      var i = nearest();
      if (!centerMode && max > 0 && track.scrollLeft >= max - 2) i = slides.length - 1;
      index = i;
      if (target !== null && Math.abs(track.scrollLeft - targetFor(target)) <= 2) target = null;

      for (var k = 0; k < slides.length; k++) {
        slides[k].classList.toggle('is-active', k === i);
      }

      var prevOff = centerMode ? i <= 0 : track.scrollLeft <= 2;
      var nextOff = centerMode ? i >= slides.length - 1 : max <= 0 || track.scrollLeft >= max - 2;
      /* Un bottone che diventa disabled perde il fuoco, e chi usa la tastiera si ritrova
         sul <body>: prima di spegnerlo il fuoco passa alla freccia opposta. */
      var active = document.activeElement;
      if (next && nextOff && active === next && prev && !prevOff) prev.focus({ preventScroll: true });
      if (prev && prevOff && active === prev && next && !nextOff) next.focus({ preventScroll: true });
      if (prev) prev.disabled = prevOff;
      if (next) next.disabled = nextOff;
      dots.forEach(function (dot, k) {
        dot.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
      if (thumbs && dots[i] && i !== revealed) {
        revealed = i;
        reveal(dots[i]);
      }
    }

    /* Si sposta solo la fila delle miniature, mai la pagina: scrollIntoView farebbe saltare
       anche lo schermo in verticale. */
    function reveal(dot) {
      var box = dot.parentElement;
      if (!box) return;
      var b = box.getBoundingClientRect();
      var d = dot.getBoundingClientRect();
      if (box.scrollWidth > box.clientWidth + 1) {
        if (d.left < b.left) box.scrollLeft += d.left - b.left;
        else if (d.right > b.right) box.scrollLeft += d.right - b.right;
      } else if (box.scrollHeight > box.clientHeight + 1) {
        if (d.top < b.top) box.scrollTop += d.top - b.top;
        else if (d.bottom > b.bottom) box.scrollTop += d.bottom - b.bottom;
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(render);
    }

    function goTo(i, smooth) {
      i = clamp(i, 0, slides.length - 1);
      index = i;
      var animate = smooth && !reduceMotion.matches;
      target = animate ? i : null;
      track.scrollTo({ left: targetFor(i), behavior: animate ? 'smooth' : 'auto' });
      schedule();
    }

    function from() {
      return target !== null ? target : index;
    }

    /* Giro automatico: data-mrd-autoplay="secondi" (04/10/2026, recensioni della home: «vadano
       avanti da sole ogni 5 secondi da quando uno arriva in questa sezione»). Il conto parte
       quando la fila e' per meta' a schermo e riparte da capo a ogni tocco, clic o rotella: il
       dito vince sempre. Fermo col mouse sopra, col fuoco da tastiera dentro, a scheda nascosta
       e per chi ha chiesto meno movimento al sistema. Dopo l'ultima torna alla prima. */
    var seconds = parseFloat(root.getAttribute('data-mrd-autoplay')) || 0;
    var timer = 0;
    var onScreen = false;
    var hover = false;
    var focus = false;
    var held = false;
    var io = null;

    function plan() {
      window.clearTimeout(timer);
      timer = 0;
      if (!seconds || !onScreen || hover || focus || held || document.hidden || reduceMotion.matches) return;
      timer = window.setTimeout(function () {
        if (maxScroll() > 0) {
          var following = from() + 1;
          goTo(following > slides.length - 1 ? 0 : following, true);
        }
        plan();
      }, seconds * 1000);
    }

    function release() {
      if (!held) return;
      held = false;
      plan();
    }

    offs.push(on(track, 'scroll', schedule, { passive: true }));
    /* Il dito o la rotella vincono sempre su uno scorrimento lanciato da una freccia. */
    offs.push(on(track, 'pointerdown', function () { target = null; }, { passive: true }));
    offs.push(on(track, 'wheel', function () { target = null; }, { passive: true }));
    if (prev) offs.push(on(prev, 'click', function () { goTo(from() - 1, true); plan(); }));
    if (next) offs.push(on(next, 'click', function () { goTo(from() + 1, true); plan(); }));
    dots.forEach(function (dot, k) {
      offs.push(on(dot, 'click', function () { goTo(k, true); plan(); }));
    });

    if (seconds) {
      /* Finche' il dito e' giu' il giro aspetta. Quando il telefono si prende il gesto per
         scorrere, al posto di pointerup arriva pointercancel: valgono tutti e due. */
      offs.push(on(track, 'pointerdown', function () { held = true; plan(); }, { passive: true }));
      offs.push(on(window, 'pointerup', release, { passive: true }));
      offs.push(on(window, 'pointercancel', release, { passive: true }));
      offs.push(on(track, 'wheel', plan, { passive: true }));
      /* pointerType === 'mouse': su telefono pointerenter arriva col tocco e pointerleave non
         arriva mai, e il giro si fermerebbe per sempre. */
      offs.push(on(root, 'pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = true; plan(); } }));
      offs.push(on(root, 'pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = false; plan(); } }));
      /* Solo il fuoco da tastiera: il tocco su un trattino non deve fermare il giro. */
      offs.push(on(root, 'focusin', function (e) {
        focus = !!(e.target && e.target.matches && e.target.matches(':focus-visible'));
        plan();
      }));
      offs.push(on(root, 'focusout', function (e) {
        if (!root.contains(e.relatedTarget)) { focus = false; plan(); }
      }));
      offs.push(on(document, 'visibilitychange', plan));
      io = new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
        plan();
      }, { threshold: 0.5 });
      io.observe(track);
    }

    /* Se cambia la LARGHEZZA (telefono girato, finestra stretta) la slide corrente si
       riallinea; i cambi di sola altezza (la barra del browser che compare) non contano. */
    var ro = new ResizeObserver(function () {
      var width = track.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      goTo(index, false);
    });

    lastWidth = track.clientWidth;
    goTo(index, false);
    ro.observe(track);

    return {
      show: function (element) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i] === element || slides[i].contains(element)) {
            goTo(i, true);
            return;
          }
        }
      },
      destroy: function () {
        ro.disconnect();
        if (io) io.disconnect();
        window.clearTimeout(timer);
        if (frame) window.cancelAnimationFrame(frame);
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Elementi sovrapposti che si alternano con dissolvenza (testimonianze, barra annunci)
     --------------------------------------------------------------------------- */
  function createFader(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-fade-item]'));
    if (items.length < 2) return null;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-dot]'));
    var seconds = parseFloat(root.getAttribute('data-mrd-autoplay')) || 0;
    var index = 0;
    var timer = 0;
    var hover = false;
    var focus = false;
    var held = false;
    var onScreen = true;
    var start = null;
    var offs = [];

    items.forEach(function (item, k) {
      if (item.classList.contains('is-active')) index = k;
    });

    function show(i) {
      index = (i + items.length) % items.length;
      items.forEach(function (item, k) {
        var active = k === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach(function (dot, k) {
        dot.setAttribute('aria-current', k === index ? 'true' : 'false');
      });
    }

    /* Il giro automatico si ferma quando c'e' qualcuno sopra col mouse, quando dentro c'e'
       il fuoco da tastiera, quando la sezione e' fuori schermo o la scheda e' nascosta, e
       per chi ha chiesto meno movimento al sistema. */
    function plan() {
      window.clearTimeout(timer);
      timer = 0;
      if (!seconds || hover || focus || held || !onScreen || document.hidden || reduceMotion.matches) return;
      timer = window.setTimeout(function () {
        show(index + 1);
        plan();
      }, seconds * 1000);
    }

    dots.forEach(function (dot, k) {
      offs.push(on(dot, 'click', function () { show(k); plan(); }));
    });

    /* pointerType === 'mouse': su telefono pointerenter arriva col tocco e pointerleave
       non arriva mai, e il giro si fermerebbe per sempre. */
    offs.push(on(root, 'pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = true; plan(); } }));
    offs.push(on(root, 'pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = false; plan(); } }));
    offs.push(on(root, 'focusin', function () { focus = true; plan(); }));
    offs.push(on(root, 'focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { focus = false; plan(); }
    }));

    if (root.hasAttribute('data-mrd-swipe')) {
      offs.push(on(root, 'pointerdown', function (e) {
        /* Solo il primo dito: con due dita e' uno zoom, non un cambio di testimonianza. */
        if (e.pointerType === 'mouse' || !e.isPrimary) return;
        start = { id: e.pointerId, x: e.clientX, y: e.clientY };
      }));
      offs.push(on(root, 'pointerup', function (e) {
        if (!start || e.pointerId !== start.id) return;
        var dx = e.clientX - start.x;
        var dy = e.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          show(index + (dx < 0 ? 1 : -1));
          plan();
        }
      }));
      offs.push(on(root, 'pointercancel', function () { start = null; }));
    }

    var io = new IntersectionObserver(function (entries) {
      onScreen = entries[entries.length - 1].isIntersecting;
      plan();
    });
    io.observe(root);
    offs.push(on(document, 'visibilitychange', plan));

    show(index);
    plan();

    return {
      hold: function (element) {
        for (var i = 0; i < items.length; i++) {
          if (items[i] === element || items[i].contains(element)) show(i);
        }
        held = true;
        plan();
      },
      release: function () {
        held = false;
        plan();
      },
      destroy: function () {
        window.clearTimeout(timer);
        io.disconnect();
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Nastro scorrevole: velocita' costante in pixel al secondo, qualunque sia la lunghezza
     del contenuto. La durata dell'animazione CSS si ricava dalla larghezza di una meta'.
     --------------------------------------------------------------------------- */
  function createMarquee(root) {
    var track = root.querySelector('[data-mrd-marquee-track]');
    var half = track && track.firstElementChild;
    if (!half) return null;

    var speed = Math.max(5, parseFloat(root.getAttribute('data-mrd-speed')) || 40);
    var lastWidth = 0;

    function measure() {
      var width = half.getBoundingClientRect().width;
      if (!width || Math.abs(width - lastWidth) < 1) return;
      lastWidth = width;
      root.style.setProperty('--mrd-mq-duration', (width / speed).toFixed(2) + 's');
    }

    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    var ro = new ResizeObserver(measure);
    ro.observe(half);

    /* Fuori schermo l'animazione si ferma: non serve muovere quello che non si vede. */
    var io = new IntersectionObserver(function (entries) {
      root.classList.toggle('is-offscreen', !entries[entries.length - 1].isIntersecting);
    });
    io.observe(root);

    return {
      destroy: function () {
        ro.disconnect();
        io.disconnect();
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Video: partono muti quando sono in vista per almeno il 60%, si fermano quando escono.
     Il tasto dell'audio accende quello di UN video e spegne gli altri.
     --------------------------------------------------------------------------- */
  function createVideos(root) {
    var videos = Array.prototype.slice.call(root.querySelectorAll('video[data-mrd-video]'));
    if (!videos.length) return null;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-sound]'));
    var offs = [];

    function videoOf(element) {
      var frame = element.closest('[data-mrd-video-frame]');
      return frame ? frame.querySelector('video[data-mrd-video]') : null;
    }

    function buttonOf(video) {
      var frame = video.closest('[data-mrd-video-frame]');
      return frame ? frame.querySelector('[data-mrd-sound]') : null;
    }

    function paint(video) {
      var button = buttonOf(video);
      if (!button) return;
      var sound = !video.muted;
      button.setAttribute('aria-pressed', sound ? 'true' : 'false');
      button.setAttribute('aria-label', sound ? testo('audioOff', 'Disattiva l’audio') : testo('audioOn', 'Attiva l’audio'));
      button.classList.toggle('is-on', sound);
    }

    function play(video) {
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(function () {});
    }

    /* «In vista» = almeno il 60% della larghezza E il 60% dell'altezza che lo schermo puo'
       mostrare. Col solo rapporto d'area, a telefono in orizzontale un video 9:16 e' piu' alto
       dello schermo e non arriverebbe mai al 60%: non partirebbe mai. La larghezza tiene
       fermi i video che spuntano ai lati. */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        var box = entry.boundingClientRect;
        var seen = entry.intersectionRect;
        var usableHeight = entry.rootBounds ? Math.min(box.height, entry.rootBounds.height) : box.height;
        var visible = entry.isIntersecting && seen.width >= box.width * 0.6 && seen.height >= usableHeight * 0.6;
        if (visible) {
          if (!reduceMotion.matches) play(video);
        } else {
          if (!video.muted) {
            video.muted = true;
            paint(video);
          }
          if (!video.paused) video.pause();
        }
      });
    }, { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] });

    videos.forEach(function (video) {
      video.muted = true;
      io.observe(video);
    });

    buttons.forEach(function (button) {
      offs.push(on(button, 'click', function () {
        var video = videoOf(button);
        if (!video) return;
        /* Il tasto di un video che spunta di lato e' toccabile: prima lo si porta al centro,
           se no si sentirebbe l'audio di un video quasi tutto fuori schermo. Quando entra in
           vista parte da solo, gia' con l'audio acceso. */
        var scroller = root.__mrd && root.__mrd.scroller;
        if (video.paused && scroller) scroller.show(button);
        var turnOn = video.muted;
        videos.forEach(function (other) {
          if (other !== video && !other.muted) {
            other.muted = true;
            paint(other);
          }
        });
        video.muted = !turnOn;
        if (turnOn) play(video);
        paint(video);
      }));
    });

    return {
      destroy: function () {
        io.disconnect();
        offs.forEach(function (off) { off(); });
        videos.forEach(function (video) { video.pause(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Prima/dopo. Col mouse si trascina da subito; col dito si aspetta di capire la
     direzione: un gesto verticale resta uno scorrimento della pagina, uno orizzontale
     muove la linea, un tocco fermo la porta in quel punto. Da tastiera: frecce, Home, Fine.
     --------------------------------------------------------------------------- */
  function createCompare(root) {
    var area = root.querySelector('[data-mrd-compare-area]');
    var handle = root.querySelector('[data-mrd-compare-handle]');
    if (!area || !handle) return null;

    var position = clamp(parseFloat(root.getAttribute('data-mrd-start')) || 50, 0, 100);
    var pointer = null;
    var frame = 0;
    var pending = null;
    var offs = [];

    /* Il disegno si fa una volta per fotogramma: col dito arrivano decine di pointermove al
       secondo e ridisegnare a ognuno faceva scattare la linea (sua segnalazione, 18/09/2026). */
    function paint() {
      frame = 0;
      if (pending === null) return;
      position = pending;
      pending = null;
      area.style.setProperty('--mrd-pos', position + '%');
      handle.setAttribute('aria-valuenow', String(Math.round(position)));
    }

    function set(value, animate) {
      pending = clamp(value, 0, 100);
      /* Con l'animazione (tocco secco, tastiera) la linea scivola; trascinando deve seguire il dito. */
      area.classList.toggle('is-sliding', !!animate && !reduceMotion.matches);
      if (!frame) frame = window.requestAnimationFrame(paint);
    }

    function fromX(x) {
      var rect = area.getBoundingClientRect();
      return rect.width ? ((x - rect.left) / rect.width) * 100 : position;
    }

    function capture(id) {
      try { area.setPointerCapture(id); } catch (err) { /* il puntatore e' gia' stato rilasciato */ }
    }

    offs.push(on(area, 'pointerdown', function (e) {
      if (!e.isPrimary) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, drag: false };
      /* Partendo dalla maniglia si trascina subito, anche col dito: e' il comando, non la foto. */
      var onHandle = e.target.closest && e.target.closest('[data-mrd-compare-handle]');
      if (e.pointerType === 'mouse' || onHandle) {
        pointer.drag = true;
        area.classList.add('is-dragging');
        capture(e.pointerId);
        set(fromX(e.clientX));
        e.preventDefault();
      }
    }));

    offs.push(on(area, 'pointermove', function (e) {
      if (!pointer || e.pointerId !== pointer.id) return;
      if (!pointer.drag) {
        var dx = Math.abs(e.clientX - pointer.x);
        var dy = Math.abs(e.clientY - pointer.y);
        /* Fuori dalla maniglia (cioe' sulla foto) bastano 3px in orizzontale, e si molla solo
           se il gesto e' CHIARAMENTE verticale: cosi' anche un trascinamento in diagonale
           muove la linea. Il gesto sicuro resta quello che parte dalla striscia della maniglia,
           dove il browser non puo' rubarlo. */
        if (dy > 18 && dy > dx * 2) { pointer = null; return; }
        if (dx < 3) return;
        pointer.drag = true;
        area.classList.add('is-dragging');
        capture(e.pointerId);
      }
      set(fromX(e.clientX));
    }));

    offs.push(on(area, 'pointerup', function (e) {
      if (!pointer || e.pointerId !== pointer.id) return;
      /* Un tocco secco porta la linea in quel punto, scivolando. */
      if (!pointer.drag) set(fromX(e.clientX), true);
      pointer = null;
      area.classList.remove('is-dragging');
    }));

    offs.push(on(area, 'pointercancel', function () {
      pointer = null;
      area.classList.remove('is-dragging');
    }));
    offs.push(on(area, 'dragstart', function (e) { e.preventDefault(); }));

    offs.push(on(handle, 'keydown', function (e) {
      var step = e.shiftKey ? 10 : 5;
      var value = null;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') value = position - step;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') value = position + step;
      else if (e.key === 'Home') value = 0;
      else if (e.key === 'End') value = 100;
      if (value === null) return;
      e.preventDefault();
      set(value, true);
    }));

    set(position);

    return {
      destroy: function () {
        if (frame) window.cancelAnimationFrame(frame);
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Menu a scomparsa dell'header. Lo stesso tasto apre e chiude (diventa una ×).
     Il pannello comincia SOTTO la barra dell'header, misurata al momento dell'apertura.
     --------------------------------------------------------------------------- */
  function createHeader(root) {
    var opener = root.querySelector('[data-mrd-menu-open]');
    var menu = root.querySelector('[data-mrd-menu]');
    if (!opener || !menu) return null;

    var panel = menu.querySelector('[data-mrd-menu-panel]') || menu;
    var bar = root.querySelector('[data-mrd-header-bar]') || root;
    var html = document.documentElement;
    var lastFocus = null;
    var hideTimer = 0;
    var locked = false;
    var offs = [];

    /* Altezza di tutta l'intestazione (header + barra annunci) scritta sul <body>: gli
       elementi appiccicati di Horizon (riepilogo del carrello, dettagli prodotto) la usano per
       fermarsi SOTTO l'intestazione invece che dietro. Horizon la calcolava solo per il suo
       header, che qui non c'e'. */
    /* MERIDIANO (04/10/2026): qui resta in alto SOLO la barra dell'intestazione (la barra annunci
       e le tab scorrono via), e #header-group e' `display: contents` (vedi mrd-header.liquid):
       il suo rettangolo misura zero. Si misura quindi la barra, che e' la parte appiccicata. */
    var groupObserver = new ResizeObserver(function () {
      document.body.style.setProperty('--header-group-height', Math.round(bar.getBoundingClientRect().height) + 'px');
    });
    groupObserver.observe(bar);

    function isOpen() {
      return opener.getAttribute('aria-expanded') === 'true';
    }

    function place() {
      var bottom = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
      menu.style.setProperty('--mrd-menu-top', bottom + 'px');
    }

    function open(fromKeyboard) {
      window.clearTimeout(hideTimer);
      lastFocus = document.activeElement;
      place();
      menu.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      opener.setAttribute('aria-label', 'Chiudi il menu');
      root.classList.add('is-menu-open');
      /* scroll-lock e' il blocco dello scorrimento di Horizon: ferma sia la pagina sia
         .page-wrapper, che da 990px in su e' il vero contenitore che scorre. */
      if (!html.hasAttribute('scroll-lock')) {
        html.setAttribute('scroll-lock', '');
        locked = true;
      }
      /* Leggere offsetWidth obbliga il browser a disegnare prima lo stato chiuso: cosi' la
         transizione verso «aperto» parte davvero, senza dipendere da requestAnimationFrame
         (che in una scheda in secondo piano puo' non arrivare mai). */
      void menu.offsetWidth;
      menu.setAttribute('data-open', '');
      /* Il fuoco entra nel menu solo se lo si e' aperto da tastiera: col dito o col mouse
         il contorno del fuoco sulla prima voce sembrerebbe un doppio filetto. */
      if (fromKeyboard) {
        var first = panel.querySelector('a[href], button:not([disabled])');
        if (first) first.focus({ preventScroll: true });
      }
    }

    function close(restoreFocus) {
      if (!isOpen()) return;
      menu.removeAttribute('data-open');
      opener.setAttribute('aria-expanded', 'false');
      opener.setAttribute('aria-label', 'Apri il menu');
      root.classList.remove('is-menu-open');
      if (locked) {
        html.removeAttribute('scroll-lock');
        locked = false;
      }
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(function () {
        if (!isOpen()) menu.hidden = true;
      }, reduceMotion.matches ? 0 : 260);
      if (restoreFocus !== false && lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    /* event.detail vale 0 quando il «clic» arriva da Invio o Spazio sulla tastiera. */
    offs.push(on(opener, 'click', function (e) {
      if (isOpen()) close(); else open(e.detail === 0);
    }));

    menu.querySelectorAll('[data-mrd-menu-close]').forEach(function (element) {
      offs.push(on(element, 'click', function () { close(); }));
    });

    panel.querySelectorAll('a[href]').forEach(function (link) {
      offs.push(on(link, 'click', function () { close(false); }));
    });

    offs.push(on(document, 'keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      /* Il fuoco gira fra la × e le voci del menu, senza scappare nella pagina sotto. */
      var focusables = [opener].concat(Array.prototype.slice.call(panel.querySelectorAll('a[href], button:not([disabled])')));
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }));

    offs.push(on(window, 'resize', function () { if (isOpen()) place(); }, { passive: true }));

    return {
      destroy: function () {
        close(false);
        window.clearTimeout(hideTimer);
        if (groupObserver) groupObserver.disconnect();
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Scheda prodotto: scelta della variante. Le opzioni sono gruppi di radio
     [data-mrd-option]; le varianti arrivano da <script data-mrd-variants>. Cambiando scelta si
     aggiornano id nel modulo, prezzi [data-mrd-price], prezzo barrato [data-mrd-compare-price], testo e
     stato del bottone, indirizzo (?variant=) e la foto della variante nella galleria.
     --------------------------------------------------------------------------- */
  function createProduct(root) {
    var data = root.querySelector('script[data-mrd-variants]');
    var form = root.querySelector('[data-mrd-product-form]');
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-option]'));
    if (!data || !form || !groups.length) return null;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (err) { return null; }

    /* id, prezzi e bottoni si cercano in tutta la pagina: la fascia scura d'acquisto e la barra
       fissa sono altre sezioni (o fuori dal modulo) ma vendono la stessa variante. */
    var gallery = root.querySelector('[data-mrd-gallery]');
    function all(selector) {
      return Array.prototype.slice.call(document.querySelectorAll(selector));
    }
    var offs = [];

    function chosen() {
      return groups.map(function (group) {
        var input = group.querySelector('input:checked');
        return input ? input.value : null;
      });
    }

    function find(values) {
      for (var k = 0; k < variants.length; k++) {
        var options = variants[k].options;
        var same = true;
        for (var j = 0; j < values.length; j++) {
          if (options[j] !== values[j]) { same = false; break; }
        }
        if (same) return variants[k];
      }
      return null;
    }

    function update() {
      var variant = find(chosen());

      /* Il nome del colore scelto, accanto all'etichetta dell'opzione (Meridiano, 04/10/2026). */
      groups.forEach(function (group) {
        var name = group.querySelector('[data-mrd-chosen]');
        var input = group.querySelector('input:checked');
        if (name && input) name.textContent = input.value;
      });

      if (variant) {
        all('input[data-mrd-variant-id]').forEach(function (el) { el.value = variant.id; });
        all('[data-mrd-price]').forEach(function (el) { el.textContent = variant.price; });
        /* data-mrd-compare-price, non data-mrd-compare: quello e' la radice del Prima/dopo, che
           cosi' veniva svuotata al cambio variante (trovato in revisione, 17/09/2026). */
        all('[data-mrd-compare-price]').forEach(function (el) {
          el.textContent = variant.compare || '';
          el.hidden = !variant.compare;
        });
        /* Con data-template («Sconto del [n]%») si tiene quella frase; senza, il solito «−n%». */
        all('[data-mrd-save]').forEach(function (el) {
          var template = el.getAttribute('data-template');
          el.textContent = template ? template.replace('[n]', variant.save) : '−' + variant.save + '%';
          el.hidden = !variant.save;
        });
      }

      /* «N pezzi rimasti» col quadratino rosso (07/10/2026): il numero e' scritto in Shopify
         sulla variante (campo «Pezzi rimasti»), quindi cambia col colore. Zero = riga nascosta. */
      var pezzi = root.querySelector('[data-mrd-pezzi]');
      if (pezzi) {
        var quanti = variant && variant.pezzi > 0 ? variant.pezzi : 0;
        var pezziTesto = pezzi.querySelector('[data-mrd-pezzi-testo]');
        pezzi.hidden = !quanti;
        if (quanti && pezziTesto) {
          pezziTesto.textContent = quanti === 1
            ? (pezzi.getAttribute('data-uno') || '')
            : (pezzi.getAttribute('data-text') || '').replace('[n]', quanti);
        }
      }

      all('[data-mrd-atc]').forEach(function (el) { el.disabled = !variant || !variant.available; });
      var text = !variant
        ? root.getAttribute('data-text-unavailable')
        : variant.available ? root.getAttribute('data-text-add') : root.getAttribute('data-text-soldout');
      /* Un bottone con un suo testo (data-text-add sull'etichetta, es. fascia scura) lo tiene. */
      all('[data-mrd-atc-label]').forEach(function (el) {
        el.textContent = variant && variant.available && el.hasAttribute('data-text-add') ? el.getAttribute('data-text-add') : text;
      });

      if (variant && !(window.Shopify && window.Shopify.designMode)) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      fotoDi(variant, true);
    }

    /* FOTO PER COLORE (07/10/2026) — «quando clicco una colorazione vedo solo le foto di quella
       colorazione». Ogni slide e ogni miniatura porta data-mrd-gruppo, il colore a cui
       appartiene («*» = di tutti i colori): lo scrive sections/mrd-product.liquid, dove sta
       anche la regola. Al cambio colore le altre si nascondono e la galleria riparte dalla
       foto di quel colore; la fila che scorre conta solo le slide visibili, quindi si rifa'.
       Per non far vedere il riquadro vuoto fra un colore e l'altro, la prima foto del colore
       nuovo si scarica mentre e' ancora nascosta e lo scambio aspetta che sia pronta (al
       massimo 700 ms): nel frattempo resta quella di prima.
       Se nessuna foto ha un colore (prodotto senza foto sulle varianti) vale il comportamento
       di sempre: la galleria scorre fino alla foto della variante. */
    var track = gallery && gallery.querySelector('[data-mrd-track]');
    var pezzi = gallery ? Array.prototype.slice.call(gallery.querySelectorAll('[data-mrd-gruppo]')) : [];
    var perColore = pezzi.some(function (el) { return el.getAttribute('data-mrd-gruppo') !== '*'; });
    var vuota = gallery && gallery.querySelector('[data-mrd-vuota]');
    var filaMiniature = gallery && gallery.querySelector('[data-mrd-thumb-row]');
    var mostrato = gallery ? gallery.getAttribute('data-mrd-colore') : null;
    var giro = 0;

    function delColore(el, colore) {
      var g = el.getAttribute('data-mrd-gruppo');
      return g === '*' || g === colore;
    }

    /* Una foto «in attesa» (snippets/mrd-image.liquid) riprende i suoi indirizzi veri. */
    function sveglia(img, prima) {
      if (!img) return;
      if (img.hasAttribute('data-mrd-defer')) {
        var s = img.getAttribute('data-mrd-srcset');
        var u = img.getAttribute('data-mrd-src');
        img.removeAttribute('data-mrd-defer');
        if (s) { img.setAttribute('srcset', s); img.removeAttribute('data-mrd-srcset'); }
        if (u) { img.setAttribute('src', u); img.removeAttribute('data-mrd-src'); }
      }
      img.setAttribute('loading', 'eager');
      if (prima) img.setAttribute('fetchpriority', 'high');
    }

    function pronta(img) {
      return new Promise(function (resolve) {
        var fatto = false;
        function ok() { if (!fatto) { fatto = true; resolve(); } }
        window.setTimeout(ok, 700);
        if (!img || (img.complete && img.naturalWidth)) { ok(); return; }
        if (img.decode) img.decode().then(ok, ok);
        else { img.addEventListener('load', ok, { once: true }); img.addEventListener('error', ok, { once: true }); }
      });
    }

    function rifaiGalleria(start) {
      var reg = gallery.__mrd || (gallery.__mrd = {});
      if (reg.scroller) { reg.scroller.destroy(); delete reg.scroller; }
      gallery.setAttribute('data-mrd-start', String(Math.max(0, start)));
      track.scrollLeft = 0;
      var nuovo = createScroller(gallery);
      if (nuovo) reg.scroller = nuovo;
    }

    function fotoDi(variant, aspetta) {
      if (!gallery || !track) return;
      var scroller = gallery.__mrd && gallery.__mrd.scroller;
      var slide = variant && variant.media ? gallery.querySelector('[data-media-id="' + variant.media + '"]') : null;
      var colore = variant ? variant.gruppo : null;
      if (!perColore || colore === mostrato) {
        if (slide && !slide.hidden && scroller) scroller.show(slide);
        return;
      }

      var mio = ++giro;
      var slides = pezzi.filter(function (el) { return el.parentElement === track; });
      var sue = slides.filter(function (el) { return delColore(el, colore); });
      var prima = slide && sue.indexOf(slide) > -1 ? slide : sue[0];
      sue.slice(0, 3).forEach(function (el) { sveglia(el.querySelector('img'), el === prima); });
      if (prima) sveglia(prima.querySelector('img'), true);

      function cambia() {
        if (mio !== giro) return;
        mostrato = colore;
        gallery.setAttribute('data-mrd-colore', colore || '');
        var quale = 0;
        var miniatura = 0;
        pezzi.forEach(function (el) {
          var si = delColore(el, colore);
          el.hidden = !si;
          el.classList.remove('is-active');
          if (!si) return;
          if (el.parentElement === track) {
            quale++;
            el.setAttribute('aria-label', quale + ' di ' + sue.length);
          } else {
            miniatura++;
            el.setAttribute('aria-label', 'Mostra la foto ' + miniatura);
          }
        });
        if (vuota) vuota.hidden = sue.length > 0;
        if (filaMiniature) { filaMiniature.hidden = sue.length < 2; filaMiniature.scrollLeft = 0; }
        rifaiGalleria(sue.indexOf(prima));
      }

      if (aspetta && prima) pronta(prima.querySelector('img')).then(cambia);
      else cambia();
    }

    /* Tornando indietro il browser puo' ricordare un colore diverso da quello con cui la
       pagina e' stata disegnata: la galleria si riallinea alla scelta, senza attese. */
    if (perColore) {
      var allAvvio = find(chosen());
      if (allAvvio && allAvvio.gruppo !== mostrato) fotoDi(allAvvio, false);
    }

    groups.forEach(function (group) {
      offs.push(on(group, 'change', update));
    });

    /* La freccia a destra della fila dei colori (impostazione «essenziale», 05/10/2026): c'e'
       solo se la fila non ci sta tutta e non si e' ancora arrivati in fondo; al tocco la fila
       scorre di tre riquadri. */
    var observers = [];
    Array.prototype.slice.call(root.querySelectorAll('[data-mrd-picks-next]')).forEach(function (arrow) {
      var row = arrow.parentElement && arrow.parentElement.querySelector('[data-mrd-picks]');
      if (!row) return;
      function sync() {
        arrow.hidden = row.scrollWidth <= row.clientWidth + 1 || row.scrollLeft >= row.scrollWidth - row.clientWidth - 1;
      }
      offs.push(on(arrow, 'click', function () {
        row.scrollBy({ left: 192, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }));
      offs.push(on(row, 'scroll', sync, { passive: true }));
      var ro = new ResizeObserver(sync);
      ro.observe(row);
      observers.push(ro);
      sync();
    });

    return {
      destroy: function () {
        observers.forEach(function (ro) { ro.disconnect(); });
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Barra fissa d'acquisto (scheda prodotto, telefono). Visibile quando il bottone principale
     [data-mrd-atc-main] e' gia' passato sopra lo schermo e non si vede nessun altro bottone
     d'acquisto [data-mrd-atc-watch] ne' il footer: cosi' non copre mai il bottone che ripete.
     --------------------------------------------------------------------------- */
  function createStickyBar(bar) {
    var main = document.querySelector('[data-mrd-atc-main]');
    if (!main) return null;
    var watched = Array.prototype.slice.call(document.querySelectorAll('[data-mrd-atc-watch], .mrd-ft'));
    var seen = new Set();
    /* data-mrd-sticky-bar="sempre" (scheda «essenziale», 05/10/2026): come nella sua foto il
       bottone fisso c'e' gia' in cima alla pagina, cioe' ogni volta che il bottone della scheda
       NON e' a schermo, sopra o sotto che sia. Si aspetta la prima misura, se no lampeggia. */
    var always = bar.getAttribute('data-mrd-sticky-bar') === 'sempre';
    var measured = false;

    function paint() {
      var past = main.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', (always ? measured : past) && seen.size === 0);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) seen.add(entry.target); else seen.delete(entry.target);
      });
      measured = true;
      paint();
    });
    watched.forEach(function (el) { io.observe(el); });

    /* Da 990px Horizon fa scorrere .page-wrapper e non la finestra: si ascoltano tutti e due. */
    var wrapper = document.querySelector('.page-wrapper');
    var frame = 0;
    function onScroll() {
      if (!frame) frame = window.requestAnimationFrame(function () { frame = 0; paint(); });
    }
    var offs = [on(window, 'scroll', onScroll, { passive: true })];
    if (wrapper) offs.push(on(wrapper, 'scroll', onScroll, { passive: true }));
    paint();

    return {
      destroy: function () {
        io.disconnect();
        if (frame) window.cancelAnimationFrame(frame);
        offs.forEach(function (off) { off(); });
        bar.classList.remove('is-visible');
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Carrello a tendina [data-mrd-cart] (sections/mrd-cart-drawer.liquid, dal layout).
     - Ogni modulo che manda a /cart/add viene intercettato: il pannello si apre SUBITO con la
       linea di caricamento, e la risposta di /cart/add.js porta gia' il pannello ridisegnato
       (parametro `sections`). Prezzi e totali arrivano sempre da Liquid.
     - − / + / Rimuovi: /cart/change.js, una modifica per volta (classe is-busy).
     - Ogni richiesta ha un tempo massimo; se qualcosa va storto il pannello si ridisegna dallo
       stato vero del carrello, e l'errore di Shopify (es. pezzi finiti) si mostra in cima.
     - L'icona della borsa [data-mrd-cart-open] apre il pannello invece della pagina carrello.
     --------------------------------------------------------------------------- */
  function createCart(root) {
    var panel = root.querySelector('[data-mrd-cart-panel]');
    if (!panel || !window.fetch || !window.FormData) return null;

    var html = document.documentElement;
    var sectionId = root.getAttribute('data-section-id');
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var busy = false;
    var locked = false;
    var hideTimer = 0;
    var lastFocus = null;
    /* true quando il carrello puo' essere cambiato fuori da qui (altra scheda, ritorno dalla
       cassa, pagina /cart di Horizon): alla prossima apertura dalla borsa si rilegge. */
    var dirty = false;
    var offs = [];

    function isOpen() {
      return root.hasAttribute('data-open');
    }

    function open() {
      window.clearTimeout(hideTimer);
      if (!isOpen()) lastFocus = document.activeElement;
      root.hidden = false;
      if (!html.hasAttribute('scroll-lock')) {
        html.setAttribute('scroll-lock', '');
        locked = true;
      }
      /* Come il menu: leggere offsetWidth fa partire davvero la transizione d'entrata. */
      void root.offsetWidth;
      root.setAttribute('data-open', '');
      panel.focus({ preventScroll: true });
    }

    function close(restoreFocus) {
      if (!isOpen()) return;
      root.removeAttribute('data-open');
      if (locked) {
        html.removeAttribute('scroll-lock');
        locked = false;
      }
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(function () {
        if (!isOpen()) root.hidden = true;
      }, reduceMotion.matches ? 0 : 360);
      if (restoreFocus !== false && lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    function setBusy(value) {
      busy = value;
      root.classList.toggle('is-busy', value);
      root.setAttribute('aria-busy', value ? 'true' : 'false');
    }

    /* fetch con tempo massimo: senza, perdere campo lasciava il carrello bloccato. */
    function request(url, options) {
      var controller = window.AbortController ? new AbortController() : null;
      var timer = window.setTimeout(function () { if (controller) controller.abort(); }, 12000);
      options = options || {};
      if (controller) options.signal = controller.signal;
      /* Accept: application/json solo sulle POST: sulla GET di una pagina con ?sections= fa
         rispondere 401 (misurato sulla 9292, 17/09/2026) e il pannello restava vuoto. */
      if (options.method && options.method !== 'GET') {
        options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
      }
      /* Il tempo massimo copre anche la lettura del corpo (dentro c'e' l'HTML del pannello): se la
         connessione cade a meta', l'abort interrompe anche res.json() e il lucchetto si libera. */
      return window.fetch(url, options).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          window.clearTimeout(timer);
          return { ok: res.ok, data: data };
        });
      }, function (err) {
        window.clearTimeout(timer);
        throw err;
      });
    }

    function updateCount(count) {
      document.querySelectorAll('[data-mrd-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.querySelectorAll('[data-mrd-cart-open]').forEach(function (el) {
        el.setAttribute('aria-label', count > 0 ? testo('cartCount', 'Carrello, [n] articoli').replace('[n]', count) : testo('cart', 'Carrello'));
      });
    }

    /* Sostituisce il contenuto del pannello con quello appena disegnato da Shopify, tenendo la
       posizione di scorrimento dell'elenco. Dice se ha davvero sostituito qualcosa. */
    /* LE FOTO ARRIVANO INSIEME AL RESTO (05/10/2026, «devi rendere il carrello veloce come negli
       altri store»; e' il rimedio misurato su purepetzone il 27/09/2026). Il pannello nuovo si
       legge in un pezzo STACCATO dalla pagina: le sue foto partono gia' li'. Si aspetta che
       siano pronte — al massimo 450 ms, poi si mostra comunque — e solo allora si sostituisce
       il contenuto. Prima il testo compariva subito e la foto dopo. Si guardano solo le foto
       delle righe (.mrd-cart__img): le altre sono «pigre» e farebbero aspettare a vuoto.
       Per questo `paint` risponde con una promessa: vero se ha sostituito qualcosa. */
    function fotoPronte(el) {
      var foto = Array.prototype.slice.call(el.querySelectorAll('.mrd-cart__img img, .mrd-cart__regalo-img img'));
      if (!foto.length) return Promise.resolve();
      var tutte = Promise.all(foto.map(function (img) {
        if (img.decode) return img.decode().catch(function () {});
        return new Promise(function (ok) {
          if (img.complete) return ok();
          img.onload = img.onerror = ok;
        });
      }));
      var tetto = new Promise(function (ok) { window.setTimeout(ok, 450); });
      return Promise.race([tutte, tetto]);
    }

    function paint(markup, error) {
      if (!markup) return Promise.resolve(false);
      var finto = document.createElement('div');
      finto.innerHTML = markup;
      var fresh = finto.querySelector('[data-mrd-cart-content]');
      if (!fresh || !root.querySelector('[data-mrd-cart-content]')) return Promise.resolve(false);
      return fotoPronte(fresh).then(function () { return metti(fresh, error); });
    }

    function metti(fresh, error) {
      var current = root.querySelector('[data-mrd-cart-content]');
      if (!current) return false;
      var oldList = current.querySelector('[data-mrd-cart-scroll]');
      var top = oldList ? oldList.scrollTop : 0;
      /* Il fuoco della tastiera torna sul bottone equivalente (stessa riga, stessa azione). */
      var active = document.activeElement;
      var focusKey = null;
      if (active && current.contains(active) && active.hasAttribute('data-mrd-cart-line')) {
        focusKey = { line: active.getAttribute('data-mrd-cart-line'), label: active.getAttribute('aria-label') };
      }
      var hadFocus = active && current.contains(active);
      current.replaceWith(fresh);
      /* Con il contenuto se n'e' andata anche la riga provvisoria, se c'era: al suo posto c'e'
         quella vera, identica (vedi `provvisoria`). */
      root.classList.remove('is-attesa');
      var newList = fresh.querySelector('[data-mrd-cart-scroll]');
      if (newList) newList.scrollTop = top;
      if (hadFocus && isOpen()) {
        var target = null;
        if (focusKey) {
          fresh.querySelectorAll('[data-mrd-cart-line="' + focusKey.line + '"]').forEach(function (el) {
            if (!target && el.getAttribute('aria-label') === focusKey.label && !el.disabled) target = el;
          });
        }
        (target || panel).focus({ preventScroll: true });
      }
      dirty = false;
      var message = fresh.querySelector('[data-mrd-cart-error]');
      if (message && error) {
        message.textContent = error;
        message.hidden = false;
      }
      updateCount(parseInt(fresh.getAttribute('data-count'), 10) || 0);
      decorate();
      return true;
    }

    /* --- Cio' che Liquid non puo' scrivere: la data di consegna e il conto alla rovescia.
       Si rifanno dopo OGNI ridisegno, perche' il contenuto del pannello viene sostituito. --- */

    /* «Consegna prevista 13–19 ottobre»: oggi + i giorni LAVORATIVI delle impostazioni
       (data-min, data-max). Stesso mese: «13–19 ottobre»; mesi diversi: «29 ott – 4 nov».
       LA PROMESSA DEVE ESSERE VERA: i giorni li decide lui, dai tempi delle sue policy. */
    /* I nomi dei mesi nella lingua della pagina (10/10/2026); se il browser non li sa, l'italiano. */
    var MESI = (function () {
      var it = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
      if (LINGUA_DATE === 'it-IT') return it;
      try {
        return it.map(function (_, m) { return new Date(2026, m, 15).toLocaleDateString(LINGUA_DATE, { month: 'long' }); });
      } catch (e) { return it; }
    })();

    function lavorativi(n) {
      var d = new Date();
      var fatti = 0;
      while (fatti < n) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) fatti++;
      }
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      return d;
    }

    function consegna() {
      var el = root.querySelector('[data-mrd-cart-consegna]');
      if (!el) return;
      var min = parseInt(el.getAttribute('data-min'), 10);
      var max = parseInt(el.getAttribute('data-max'), 10);
      if (isNaN(max) || max <= 0) return;
      if (isNaN(min) || min < 0 || min > max) min = max;
      var a = lavorativi(min);
      var b = lavorativi(max);
      var text;
      if (min === max) text = b.getDate() + ' ' + MESI[b.getMonth()];
      else if (a.getMonth() === b.getMonth()) text = a.getDate() + '–' + b.getDate() + ' ' + MESI[b.getMonth()];
      else text = a.getDate() + ' ' + MESI[a.getMonth()].slice(0, 3) + ' – ' + b.getDate() + ' ' + MESI[b.getMonth()].slice(0, 3);
      el.textContent = text;
      var row = el.closest('[data-mrd-cart-consegna-row]');
      if (row) row.hidden = false;
    }

    /* «Il tuo carrello è riservato per 09:59» (sua richiesta, 05/10/2026). La fine del conto
       sta nel browser (localStorage), cosi' cambiando pagina non riparte da capo. A zero la
       fascia sparisce e NON riparte: riparte solo dopo che il carrello e' stato svuotato. */
    var TIMER_KEY = 'mrd-cart-riservato';
    var timerFine = 0;

    function leggiFine() {
      try { return parseInt(window.localStorage.getItem(TIMER_KEY), 10) || timerFine; } catch (err) { return timerFine; }
    }

    function scriviFine(value) {
      timerFine = value;
      try {
        if (value) window.localStorage.setItem(TIMER_KEY, String(value));
        else window.localStorage.removeItem(TIMER_KEY);
      } catch (err) { /* navigazione privata: resta in memoria per questa pagina */ }
    }

    function timerTick() {
      var el = root.querySelector('[data-mrd-cart-timer]');
      if (!el) {
        var content = root.querySelector('[data-mrd-cart-content]');
        if (content && content.getAttribute('data-count') === '0' && leggiFine()) scriviFine(0);
        return;
      }
      var minuti = parseInt(el.getAttribute('data-minuti'), 10) || 0;
      if (!minuti) return;
      var now = Date.now();
      var fine = leggiFine();
      if (!fine) {
        fine = now + minuti * 60000;
        scriviFine(fine);
      }
      var left = Math.round((fine - now) / 1000);
      if (left <= 0) {
        el.hidden = true;
        return;
      }
      var num = el.querySelector('[data-mrd-cart-timer-num]');
      var m = Math.floor(left / 60);
      var s = left % 60;
      if (num) num.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      el.hidden = false;
    }

    function decorate() {
      consegna();
      timerTick();
      /* La fascia «Spedizione gratuita · parte entro …» (13/10/2026): la data come nella scheda. */
      shipDates(root);
      soloRegaliVeri();
      /* «Scopri la collezione» del carrello vuoto punta a /collections: qui diventa Collezione
         Uomo o Collezione Donna, come le voci di menu. Va rifatto a ogni ridisegno. */
      tieniLingua(root);
      segnaProvenienza(root);
    }

    var timerId = window.setInterval(timerTick, 1000);
    decorate();

    function refresh(error) {
      return pronta().then(function () {
        return request(window.location.pathname + '?sections=' + encodeURIComponent(sectionId), { method: 'GET' });
      })
        .then(function (r) {
          return paint(r.data && r.data[sectionId], error).then(function (ok) {
            if (!ok && error) showError(error);
          });
        });
    }

    function showError(text) {
      var message = root.querySelector('[data-mrd-cart-error]');
      if (!message) return;
      message.textContent = text;
      message.hidden = false;
    }

    function paintFrom(r) {
      return paint(r.data.sections && r.data.sections[sectionId]);
    }

    /* SOLO IN ANTEPRIMA LOCALE (`theme dev`, 127.0.0.1) — misurato il 05/10/2026 rifacendo il
       carrello. Nel browser, le richieste che fanno ridisegnare una sezione (/cart/add.js,
       /cart/change.js, ?sections=) tornano disegnate dal TEMA PUBBLICATO, non da quello che si
       sta provando, finche' quella sessione non ha chiesto una volta il tema di sviluppo con
       ?preview_theme_id=. Risultato: dopo «Aggiungi» o «+» compariva il carrello VECCHIO con
       gli stili nuovi sopra (lui: «ma che hai fatto… andava bene poi hai cambiato tutto»).
       Con Invoke-WebRequest non si vede: senza cookie la risposta e' quella giusta.
       Una richiesta a vuoto con quel parametro mette a posto la sessione; va fatta SUBITO PRIMA
       di ogni richiesta del carrello, non all'avvio: fatta all'avvio non reggeva (le altre
       richieste di apertura pagina la rimettevano com'era — provato). Costa qualche decimo di
       secondo, solo in locale. Sul sito vero e nell'anteprima da telefono questo ramo non
       parte mai: `pronta()` risponde subito. */
    var ANTEPRIMA_LOCALE = /^(127\.0\.0\.1|localhost)$/.test(window.location.hostname) &&
      !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.id && window.Shopify.theme.role !== 'main');

    function pronta() {
      if (!ANTEPRIMA_LOCALE) return Promise.resolve();
      return window.fetch(window.location.pathname + '?sections=' + encodeURIComponent(sectionId) +
        '&preview_theme_id=' + window.Shopify.theme.id).then(function () {}, function () {});
    }

    var OFFLINE = testo('offline', 'Connessione assente o lenta: riprova tra un momento.');

    function finish(promise) {
      return promise.catch(function () {
        /* La richiesta e' caduta: la riga provvisoria si toglie SUBITO, prima della rilettura
           (che puo' restare appesa altri 12 s). Trovato in revisione (08/10/2026): con la rete
           che non risponde ma non cade, la riga di un prodotto non entrato restava a schermo
           fino a 24 s, piena, col pannello che si poteva chiudere. */
        viaProvvisoria();
        return refresh(OFFLINE).catch(function () { showError(OFFLINE); });
      }).then(function () {
        /* Rete di sicurezza: se il pannello non e' stato ridisegnato, la riga provvisoria non
           deve restare li' a far credere che il prodotto sia nel carrello. */
        viaProvvisoria();
        setBusy(false);
      });
    }

    /* --- LA RIGA PROVVISORIA (08/10/2026 — «dobbiamo migliorare la velocità del carrello») ----
       Misurato sul sito vero: dal tocco alla riga 0,5-0,6 s, tutti di Shopify (/cart/add.js
       risponde dopo ~0,5 s anche senza chiedergli la sezione); il tema ne mette 0,04. Quel
       tempo non si toglie: si riempie. Nell'istante del tocco si mette nel pannello la riga del
       prodotto che sta entrando, clonata dal modello che la sezione ha stampato per quel colore
       (template[data-mrd-cart-attesa], stesso snippet della riga vera). Quando Shopify risponde
       `metti` sostituisce tutto il contenuto come sempre: la riga vera e' identica.
       REGOLE (studio 7.6, «non far cambiare il numero prima della risposta del server»):
       - NON si toccano totale, numerino della borsa, quantita'. Il piede arriva con la risposta;
       - se quel colore e' GIA' nel carrello non si fa niente (la quantita' la dice Shopify);
       - piu' di un pezzo per volta, o il modulo con le offerte: niente, si aspetta come prima;
       - la riga e' `inert` e il pannello e' occupato finche' c'e': non si puo' toccare;
       - nessuna richiesta in piu', nessun tempo in piu': e' solo quello che si vede nell'attesa;
       - se qualcosa va storto si toglie SUBITO: richiesta caduta (`finish`), rifiuto di
         Shopify (`add`), e in ogni caso alla fine;
       - PANNELLO FORSE VECCHIO (`dirty`: la scheda e' stata lasciata e ripresa, o il carrello e'
         cambiato fuori da qui): niente riga provvisoria. Con due schede aperte il pannello di
         una non sa cosa ha aggiunto l'altra: disegnerebbe «1» e un regalo dove Shopify
         rispondera' «2» e due (revisione, 08/10/2026). Dall'aggiunta dopo torna;
       - UNO SCONTO SUI PRODOTTI LA SBUGIARDA: il modello porta il prezzo della variante, la riga
         vera quello scontato e la riga dello sconto. Oggi in Shopify sconti non ce ne sono; il
         giorno che ne crea uno sui prodotti si spegne «Riga provvisoria» nella sezione
         (`show_pending`): senza modelli qui non parte niente e il carrello torna com'era;
       - con la barra del traguardo accesa (`show_goal`) i modelli non vengono stampati: la
         testata crescerebbe alla risposta. Va fatto il suo modello, il giorno che la riaccende.
       A carrello vuoto ci sono anche la riga del timer e quella del regalo (uno), che Shopify
       rimandera' uguali: cosi' all'arrivo della risposta in alto non si sposta niente. */
    function clonaModello(selettore) {
      var modello = root.querySelector(selettore);
      var primo = modello && modello.content && modello.content.firstElementChild;
      if (!primo) return null;
      var copia = primo.cloneNode(true);
      copia.setAttribute('data-mrd-cart-attesa-nodo', '');
      return copia;
    }

    /* Dice se l'ha disegnata. */
    function provvisoria(body) {
      if (dirty) return false;
      var id = body.get('id');
      if (typeof id !== 'string' || !/^\d+$/.test(id)) return false;
      var quanti = body.get('quantity');
      if (quanti && String(quanti) !== '1') return false;
      var content = root.querySelector('[data-mrd-cart-content]');
      if (!content) return false;
      var righe = content.querySelectorAll('.mrd-cart__item');
      for (var i = 0; i < righe.length; i++) {
        if ((righe[i].getAttribute('data-mrd-cart-key') || '').indexOf(id + ':') === 0) return false;
      }
      var riga = clonaModello('template[data-mrd-cart-attesa="' + id + '"]');
      if (!riga) return false;
      riga.setAttribute('inert', '');
      var lista = content.querySelector('.mrd-cart__items');
      /* SCONTO SUL SECONDO OROLOGIO (13/10/2026): con un orologio gia' nel carrello quello nuovo
         puo' far nascere lo sconto, su di lui o su un'altra riga, e il modello ha il prezzo pieno.
         Allora niente riga provvisoria: si aspetta Shopify (regola: mai numeri prima della
         risposta). A carrello vuoto il primo orologio non ha sconto: la provvisoria resta. */
      if (lista && content.hasAttribute('data-mrd-cart-sconto')) return false;
      if (lista) {
        /* Carrello con altre righe: quella nuova va in cima, dove la mette Shopify. */
        lista.insertBefore(riga, lista.firstChild);
        var elenco = content.querySelector('[data-mrd-cart-scroll]');
        if (elenco) elenco.scrollTop = 0;
      } else {
        var vuoto = content.querySelector('.mrd-cart__empty');
        if (!vuoto) return false;
        var scroll = document.createElement('div');
        scroll.className = 'mrd-cart__scroll';
        scroll.setAttribute('data-mrd-cart-attesa-nodo', '');
        lista = document.createElement('ul');
        lista.className = 'mrd-cart__items';
        lista.setAttribute('role', 'list');
        lista.appendChild(riga);
        var dati = datiRegali();
        if (dati && dati.tutti && !regaloNo && String(dati.variante) !== id) {
          var dono = clonaModello('template[data-mrd-cart-attesa-regalo]');
          if (dono) lista.appendChild(dono);
        }
        scroll.appendChild(lista);
        vuoto.parentNode.insertBefore(scroll, vuoto);
        var testa = content.querySelector('.mrd-cart__head');
        if (testa && !testa.querySelector('[data-mrd-cart-timer]')) {
          var timer = clonaModello('template[data-mrd-cart-attesa-timer]');
          var chiudi = testa.querySelector('[data-mrd-cart-close]');
          if (timer && chiudi) {
            testa.insertBefore(timer, chiudi.nextSibling);
            timerTick();
          }
        }
      }
      /* L'avviso di un errore di prima (rete caduta, poi si ritocca «Aggiungi») sta sopra
         l'elenco: lasciato li', all'arrivo della risposta spariva e la riga saltava in su di una
         quarantina di pixel (revisione, 08/10/2026). Se anche questa aggiunta va male lo
         riscrivono `showError` o il ridisegno. */
      var avviso = content.querySelector('[data-mrd-cart-error]');
      if (avviso) {
        avviso.hidden = true;
        avviso.textContent = '';
      }
      root.classList.add('is-attesa');
      return true;
    }

    function viaProvvisoria() {
      root.classList.remove('is-attesa');
      var nodi = root.querySelectorAll('[data-mrd-cart-attesa-nodo]');
      for (var i = 0; i < nodi.length; i++) {
        if (nodi[i].parentNode) nodi[i].parentNode.removeChild(nodi[i]);
      }
    }

    /* --- IL REGALO VERO (07/10/2026) -------------------------------------------------------
       Il regolatore del bracciale e' un prodotto vero a ZERO EURO (col prezzo di confronto):
       deve stare nel carrello, alla cassa e nell'ordine, UNO PER OGNI OROLOGIO COMPRATO, in
       una riga sola («se uno prende 2 orologi diversi i regolatori sono 2, ma non fare 2
       regali nel carrello, metti solo 2 nello stesso»). La regola la scrive la sezione nel
       blocco [data-mrd-cart-regali]: quale variante, quanti ne servono («voluti»), quanti ce
       ne sono («presenti»), quante righe comprate. Qui si fa solo quello che serve per
       pareggiare i due numeri.

       «FALLO MEGLIO» (dell'altro suo negozio) — veloce e preciso come prima, senza eccezioni:
       - AGGIUNTA: i regali viaggiano NELLA STESSA richiesta del prodotto (items[] di
         /cart/add.js), tanti quanti i pezzi aggiunti. Nessuna richiesta in piu', e il
         pannello che torna e' gia' giusto;
       - PIU', MENO E «RIMUOVI»: una sola /cart/update.js con la riga E i regali (vedi `change`);
       - «RIMUOVI» SULL'ULTIMO PRODOTTO: la stessa /cart/update.js, riga a 0 e regalo a 0 (se no
         resterebbe nel carrello un regalo da solo, ordinabile a zero euro). Mai /cart/clear.js;
       - IL TOTALE NON PUO' SBAGLIARE: il regalo costa zero, con o senza di lui la cifra e' la
         stessa. Per questo la sua riga puo' essere disegnata dalla regola senza aspettarlo;
       - RETE DI SICUREZZA (`allineaRegali`): se i due numeri non tornano — carrello nato prima
         del regalo, modificato da un'altra scheda o dalla pagina /cart — UNA /cart/update.js
         con la quantita' ASSOLUTA (rifarla due volte non raddoppia niente). Di norma non parte;
       - L'ACQUISTO DEL CLIENTE NON SALTA MAI PER COLPA DEL REGALO: se la richiesta col regalo
         viene rifiutata si rifa' senza, e la riga del regalo si nasconde (non si promette una
         cosa che nell'ordine non c'e').
       QUANDO LO VORRA' VENDERE: NON dare un prezzo a questo prodotto (revisione del 07/10/2026).
       Con un prezzo il blocco dei conti non viene piu' stampato e tutto questo resta fermo, ma
       chi ha gia' il regalo nel carrello se lo ritrova A PAGAMENTO senza averlo scelto. Si crea
       un prodotto NUOVO a pagamento e questo resta a zero. */
    var regaloNo = false;
    var cassaProvata = false;

    function datiRegali() {
      var el = root.querySelector('[data-mrd-cart-regali]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (err) { return null; }
    }

    /* La riga del regalo e' disegnata dalla regola: se il regalo vero non e' riuscito a
       entrare, non si mostra. */
    function soloRegaliVeri() {
      var riga = root.querySelector('[data-mrd-cart-regalo]');
      if (!riga) return;
      var dati = datiRegali();
      riga.hidden = !!(regaloNo && dati && dati.presenti < dati.voluti);
    }

    /* Il corpo dell'aggiunta con dentro anche i regali: UNO PER OGNI PEZZO che si sta
       aggiungendo (sua regola: due orologi, due regolatori). /cart/add.js SOMMA, quindi basta
       mandare tanti regali quanti sono i pezzi: se nel carrello ce n'erano gia', salgono.
       Il modulo semplice manda `id` (+ quantity, selling_plan, properties[...]): diventa
       items[0][...] e il regalo e' items[1]. Il modulo con le offerte manda gia' items[n][id]:
       il regalo si accoda. In ogni caso dubbio risponde il corpo com'era (e ai regali pensa
       dopo `allineaRegali`). `tutti` falso = solo certi tipi di prodotto danno il regalo, e da
       qui il tipo non si conosce: anche li' si lascia fare ad `allineaRegali`. */
    function conRegalo(body) {
      var dati = datiRegali();
      if (regaloNo || !dati || !dati.variante || !dati.tutti) return body;
      var regalo = String(dati.variante);
      var nuovo = new FormData();
      var posto = 0;
      var pezzi = 0;
      var sicuro = true;
      if (body.get('id')) {
        if (String(body.get('id')) === regalo) return body;
        body.forEach(function (value, key) {
          if (typeof value !== 'string') { sicuro = false; return; }
          if (key === 'id' || key === 'quantity' || key === 'selling_plan') nuovo.append('items[0][' + key + ']', value);
          else if (key.indexOf('properties[') === 0) nuovo.append('items[0][properties]' + key.slice('properties'.length), value);
          else nuovo.append(key, value);
        });
        if (!nuovo.has('items[0][quantity]')) nuovo.append('items[0][quantity]', '1');
        pezzi = parseInt(nuovo.get('items[0][quantity]'), 10);
        posto = 1;
      } else {
        var quanti = {};
        body.forEach(function (value, key) {
          if (typeof value !== 'string') { sicuro = false; return; }
          nuovo.append(key, value);
          var m = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(key);
          if (!m) return;
          if (m[2] === 'id') {
            if (String(value) === regalo) sicuro = false;
            posto = Math.max(posto, parseInt(m[1], 10) + 1);
            if (!(m[1] in quanti)) quanti[m[1]] = 1;
          } else {
            quanti[m[1]] = parseInt(value, 10);
          }
        });
        Object.keys(quanti).forEach(function (k) { pezzi += quanti[k]; });
      }
      if (!sicuro || posto === 0 || !(pezzi >= 1)) return body;
      nuovo.append('items[' + posto + '][id]', regalo);
      nuovo.append('items[' + posto + '][quantity]', String(pezzi));
      return nuovo;
    }

    function allineaRegali(giro) {
      giro = giro || 0;
      var dati = datiRegali();
      if (!dati || !dati.variante || dati.presenti === dati.voluti) return Promise.resolve();
      var manca = dati.voluti > dati.presenti;
      if (manca && regaloNo) return Promise.resolve();
      /* Due giri al massimo. Se dopo due giri il regalo non c'e' ancora, per questa pagina non
         si riprova e la sua riga si nasconde. */
      if (giro >= 2) {
        if (manca) { regaloNo = true; soloRegaliVeri(); }
        return Promise.resolve();
      }
      var updates = {};
      updates[dati.variante] = dati.voluti;
      return pronta().then(function () {
        return request(base + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates: updates, sections: sectionId, sections_url: window.location.pathname })
        });
      }).then(function (r) {
        if (!r.ok) {
          if (manca) { regaloNo = true; soloRegaliVeri(); }
          return;
        }
        return paintFrom(r).then(function (ok) { if (!ok) return refresh(); })
          .then(function () { return allineaRegali(giro + 1); });
      });
    }

    /* --- I GEMELLI A -30% (13/10/2026) ---------------------------------------------------
       Sue parole: «fai che lo sconto non va di 2 in 2 ma ogni volta che aggiungi un orologio»
       (Shopify da solo non lo sa fare: provato) e poi «si può fare che nel carrello c'è
       un'altra collezione di orologi che sono gli stessi ma con i prezzi già meno 30%?», «vai».
       Ogni orologio ha in Shopify un GEMELLO nascosto, uguale ma al 30% in meno (spiegato in
       sections/mrd-cart-drawer.liquid, «I GEMELLI A -30%»). LA REGOLA: nel carrello il primo
       orologio e' a prezzo pieno, OGNI ALTRO e' il suo gemello. Qui la si fa rispettare:
       - AGGIUNTA (`conGemelli`): se nel carrello c'e' gia' un orologio a prezzo pieno,
         l'orologio della richiesta diventa il suo gemello (la coppia sta sul modulo, in
         data-mrd-gemelli). Stessa richiesta di prima, nessun tempo in piu';
       - PIU', MENO, «RIMUOVI» (`pianoGemelli`, da `change`): si calcola il carrello come deve
         diventare — il pezzo in piu' di un orologio pieno e' il suo gemello; tolto l'ultimo
         orologio pieno, il gemello piu' caro torna pieno («se eliminano l'altro orologio il
         prezzo torna normale») — e parte tutto, regali compresi, in UNA /cart/update.js;
       - RETE DI SICUREZZA (`allineaGemelli`): conti storti (due pieni, solo gemelli, carrello
         cambiato in un'altra scheda) -> una /cart/update.js li rimette a posto. Di norma no.
       Spento in Impostazioni tema > Meridiano (`attivo` falso): niente scambi, e i gemelli
       rimasti nei carrelli tornano orologi veri alla prima visita. */
    var gemelliNo = false;

    function datiGemelli() {
      var el = root.querySelector('[data-mrd-cart-gemelli]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (err) { return null; }
    }

    function contaGemelli(g) {
      var c = { pieni: 0, gemelli: 0 };
      (g.righe || []).forEach(function (r) {
        if (r.t === 'p') c.pieni += r.q;
        if (r.t === 'g') c.gemelli += r.q;
      });
      return c;
    }

    function gemelliStorti(g) {
      g = g || datiGemelli();
      if (!g || gemelliNo) return false;
      var c = contaGemelli(g);
      return g.attivo ? (c.pieni > 1 || (c.pieni === 0 && c.gemelli > 0)) : c.gemelli > 0;
    }

    function conGemelli(body, form) {
      var g = datiGemelli();
      if (!g || !g.attivo || !form || !form.getAttribute) return body;
      /* La coppia sta sul modulo o, nel modulo della scheda (fatto dal tag `form` di Shopify), su
         un <span hidden> dentro. */
      var porta = form.hasAttribute('data-mrd-gemelli') ? form : form.querySelector('[data-mrd-gemelli]');
      var mappa = null;
      try { mappa = JSON.parse((porta && porta.getAttribute('data-mrd-gemelli')) || 'null'); } catch (err) { mappa = null; }
      if (!mappa || !body.set) return body;
      var pieni = contaGemelli(g).pieni;
      if (body.has('id')) {
        var id = String(body.get('id'));
        if (pieni > 0 && mappa[id]) body.set('id', String(mappa[id]));
        return body;
      }
      /* Piu' pezzi in una richiesta (l'abbinamento nella scheda): in ordine, il primo orologio
         resta pieno se il carrello non ne ha, gli altri diventano gemelli. */
      var posti = [];
      body.forEach(function (value, key) {
        var m = /^items\[(\d+)\]\[id\]$/.exec(key);
        if (m) posti.push(parseInt(m[1], 10));
      });
      posti.sort(function (a, b) { return a - b; });
      posti.forEach(function (n) {
        var chiave = 'items[' + n + '][id]';
        var vero = String(body.get(chiave));
        if (!mappa[vero]) return;
        if (pieni > 0) body.set(chiave, String(mappa[vero]));
        else pieni += parseInt(body.get('items[' + n + '][quantity]') || '1', 10) || 1;
      });
      return body;
    }

    /* Il carrello come deve diventare dopo il cambio chiesto (variante `vid` a `nuovi` pezzi;
       `vid` null = com'e'), con le regole dei gemelli e dei regali. Risponde con le sole
       varianti da cambiare, quantita' ASSOLUTE (rifarla non raddoppia niente); {} = gia' a
       posto; null = non si sa fare (la stessa variante su due righe: si va come prima). */
    function pianoGemelli(g, vid, nuovi) {
      var righe = g.righe || [];
      var ora = {};
      var tipo = {};
      var socio = {};
      var prezzo = {};
      for (var i = 0; i < righe.length; i++) {
        var r = righe[i];
        var v = String(r.v);
        if (v in ora) return null;
        ora[v] = r.q;
        tipo[v] = r.t;
        prezzo[v] = r.p;
        if (r.c && (r.t === 'p' || r.t === 'g')) {
          var c = String(r.c);
          socio[v] = c;
          socio[c] = v;
          if (!(c in ora)) {
            tipo[c] = r.t === 'p' ? 'g' : 'p';
            prezzo[c] = r.p;
          }
        }
      }
      var voglio = {};
      Object.keys(ora).forEach(function (k) { voglio[k] = ora[k]; });
      if (vid != null) {
        vid = String(vid);
        var prima = voglio[vid] || 0;
        voglio[vid] = nuovi;
        /* «+» su un orologio a prezzo pieno: il pezzo in piu' e' il suo gemello. */
        if (g.attivo && tipo[vid] === 'p' && socio[vid] && nuovi > prima) {
          voglio[vid] = prima;
          voglio[socio[vid]] = (voglio[socio[vid]] || 0) + (nuovi - prima);
        }
      }
      function sposta(da, quanti) {
        voglio[da] -= quanti;
        voglio[socio[da]] = (voglio[socio[da]] || 0) + quanti;
      }
      function piuCaro(t) {
        var meglio = null;
        Object.keys(voglio).forEach(function (k) {
          if (tipo[k] === t && socio[k] && voglio[k] > 0 && (meglio === null || prezzo[k] > prezzo[meglio])) meglio = k;
        });
        return meglio;
      }
      if (!g.attivo) {
        Object.keys(voglio).forEach(function (k) {
          if (tipo[k] === 'g' && socio[k] && voglio[k] > 0) sposta(k, voglio[k]);
        });
      } else {
        var pieni = 0;
        var gemelli = 0;
        Object.keys(voglio).forEach(function (k) {
          if (tipo[k] === 'p' && socio[k]) pieni += voglio[k];
          if (tipo[k] === 'g' && socio[k]) gemelli += voglio[k];
        });
        if (pieni === 0 && gemelli > 0) {
          /* Tolto l'ultimo orologio a prezzo pieno: il gemello piu' caro torna pieno. */
          sposta(piuCaro('g'), 1);
        } else if (pieni > 1) {
          /* Piu' di un orologio pieno: resta pieno UN pezzo del piu' caro, gli altri diventano gemelli. */
          var resta = piuCaro('p');
          Object.keys(voglio).forEach(function (k) {
            if (tipo[k] !== 'p' || !socio[k]) return;
            var via = voglio[k] - (k === resta ? 1 : 0);
            if (via > 0) sposta(k, via);
          });
        }
      }
      /* I regali: uno per ogni pezzo comprato (se li danno tutti; se no ci pensa `allineaRegali`). */
      var dati = datiRegali();
      if (dati && dati.variante && dati.tutti && !regaloNo) {
        var regalo = String(dati.variante);
        var tot = 0;
        Object.keys(voglio).forEach(function (k) { if (k !== regalo) tot += Math.max(0, voglio[k]); });
        ora[regalo] = dati.presenti;
        voglio[regalo] = tot;
      }
      var cambi = {};
      Object.keys(voglio).forEach(function (k) {
        if (voglio[k] < 0) voglio[k] = 0;
        if (voglio[k] !== (ora[k] || 0)) cambi[k] = voglio[k];
      });
      return cambi;
    }

    function allineaGemelli() {
      var g = datiGemelli();
      if (!gemelliStorti(g)) return Promise.resolve();
      var piano = pianoGemelli(g, null, 0);
      if (!piano || !Object.keys(piano).length) return Promise.resolve();
      return pronta().then(function () {
        return request(base + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates: piano, sections: sectionId, sections_url: window.location.pathname })
        });
      }).then(function (r) {
        /* Rifiutata: per questa pagina non si riprova (il carrello resta com'e', il totale e' quello vero). */
        if (!r.ok) { gemelliNo = true; return; }
        return paintFrom(r).then(function (ok) { if (!ok) return refresh(); });
      });
    }

    /* Prima i gemelli, poi i regali (dopo i gemelli, di norma, sono gia' giusti). */
    function allinea() {
      return allineaGemelli().then(function () { return allineaRegali(); });
    }

    /* Fuori da un'aggiunta o da una modifica (apertura della pagina, carrello cambiato
       altrove): si pareggia solo se serve, e col lucchetto come ogni altra scrittura. */
    function regaliPari() {
      var dati = datiRegali();
      return !dati || dati.presenti === dati.voluti || (regaloNo && dati.voluti > dati.presenti);
    }

    function riallinea() {
      if (busy || (regaliPari() && !gemelliStorti())) return;
      setBusy(true);
      finish(allinea());
    }

    function add(form) {
      var body = new FormData(form);
      body.append('sections', sectionId);
      body.append('sections_url', window.location.pathname);
      body = conGemelli(body, form);
      var colRegalo = conRegalo(body);
      function manda(corpo) {
        return request(base + 'cart/add.js', {
          method: 'POST',
          body: corpo,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
      }
      setBusy(true);
      /* Prima di aprire: il pannello entra gia' con dentro la riga che si sta aggiungendo. */
      var attesa = provvisoria(body);
      open();
      /* La riga nuova e' in cima: l'elenco si riporta su DOPO aver aperto. A pannello chiuso
         (display: none) scrivere scrollTop non ha effetto, e chi aveva scorso l'elenco prima di
         chiuderlo si ritrovava la riga nuova fuori vista (revisione, 08/10/2026). */
      if (attesa) {
        var elencoSu = root.querySelector('[data-mrd-cart-scroll]');
        if (elencoSu) elencoSu.scrollTop = 0;
      }
      finish(pronta().then(function () {
        return manda(colRegalo);
      }).then(function (r) {
        if (r.ok || colRegalo === body) return r;
        /* Rifiutata col regalo dentro: si riprova con il solo prodotto. Se cosi' passa, era il
           regalo: per questa pagina non lo si rimette. */
        return manda(body).then(function (solo) {
          if (solo.ok) regaloNo = true;
          return solo;
        });
      }).then(function (r) {
        if (!r.ok) {
          /* Shopify ha detto no: la riga provvisoria se ne va subito, senza aspettare la rilettura. */
          viaProvvisoria();
          return refresh(r.data.description || r.data.message || testo('addError', 'Non è stato possibile aggiungere il prodotto.'));
        }
        return paintFrom(r).then(function (ok) { if (!ok) return refresh(); });
      }).then(function () { return allinea(); }));
    }

    function change(line, quantity, riga) {
      var dati = datiRegali();
      /* I GEMELLI (13/10/2026): con le regole dei gemelli il cambio puo' toccare piu' righe
         (il gemello che nasce col «+», quello che torna pieno col «Rimuovi»): parte tutto,
         regali compresi, in una /cart/update.js con le quantita' assolute. */
      var gem = datiGemelli();
      var piano = null;
      if (gem && riga) {
        var vidRiga = (riga.getAttribute('data-mrd-cart-key') || '').split(':')[0];
        if (/^\d+$/.test(vidRiga)) piano = pianoGemelli(gem, vidRiga, quantity);
        if (piano && !Object.keys(piano).length) piano = null;
      }
      /* I REGALI SEGUONO I PEZZI (uno per orologio): con piu', meno e «Rimuovi» la quantita'
         nuova della riga e quella dei regali partono INSIEME, in una /cart/update.js — la riga
         con la sua chiave, il regalo con la sua variante, quantita' assolute. Stesso numero di
         richieste di prima, e il pannello che torna ha gia' il numero giusto di regali.
         Se la riga non da' il regalo, o il numero non cambia, resta la /cart/change.js di sempre.
         «RIMUOVI» SULL'ULTIMA RIGA passa di qui come gli altri (riga a 0, regalo a 0): MAI
         /cart/clear.js. C'era, ed e' stato tolto in revisione (07/10/2026): clear.js cancella
         tutto il carrello VERO, non quello che il pannello conosce. Con due schede aperte
         (Tirreno aggiunto nella prima, Augusto nella seconda) «Rimuovi» sul Tirreno nella prima,
         rimasta col pannello vecchio, portava via anche l'Augusto. Cosi' si toccano solo quella
         riga e il regalo: il resto rimane, e `allineaRegali` rimette i regali giusti. */
      var insieme = null;
      if (dati && dati.variante && !regaloNo && riga) {
        var chiave = riga.getAttribute('data-mrd-cart-key');
        var ora = parseInt(riga.getAttribute('data-mrd-cart-ora'), 10);
        if (chiave && !isNaN(ora)) {
          var regali = dati.voluti + (riga.getAttribute('data-mrd-cart-dono') === '1' ? quantity - ora : 0);
          if (regali < 0) regali = 0;
          if (regali !== dati.presenti) {
            insieme = {};
            insieme[chiave] = quantity;
            insieme[dati.variante] = regali;
          }
        }
      }
      function manda(percorso, corpo) {
        corpo.sections = sectionId;
        corpo.sections_url = window.location.pathname;
        return request(base + percorso, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(corpo)
        });
      }
      function solaRiga() { return manda('cart/change.js', { line: line, quantity: quantity }); }
      setBusy(true);
      finish(pronta().then(function () {
        if (piano) {
          return manda('cart/update.js', { updates: piano }).then(function (r) {
            return r.ok ? r : solaRiga();
          });
        }
        if (!insieme) return solaRiga();
        /* La modifica del cliente non salta mai per colpa del regalo: se la richiesta doppia
           viene rifiutata, si rifa' con la sola riga (ai regali pensa dopo `allineaRegali`). */
        return manda('cart/update.js', { updates: insieme }).then(function (r) {
          return r.ok ? r : solaRiga();
        });
      }).then(function (r) {
        if (!r.ok) return refresh(r.data.description || r.data.message || testo('changeError', 'Modifica non riuscita.'));
        return paintFrom(r).then(function (ok) { if (!ok) return refresh(); });
      }).then(function () { return allinea(); }));
    }

    offs.push(on(document, 'submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      /* I moduli di Horizon (aggiunta rapida nelle schede prodotto delle pagine elenco) fanno gia'
         la loro richiesta: intercettarli metteva DUE pezzi nel carrello (trovato in revisione,
         17/09/2026). A quelli pensa l'evento del carrello qui sotto. */
      if (e.defaultPrevented || form.closest('product-form-component')) return;
      e.preventDefault();
      if (busy) return;
      add(form);
    }));

    /* Aggiunte e modifiche fatte dai componenti di Horizon: si ridisegna il pannello; se arrivano
       da un'aggiunta rapida lo si apre anche, come per i nostri bottoni. */
    /* Horizon manda l'evento PRIMA della sua richiesta (misurato: evento a 2 ms, /cart/add.js a
       7 ms), con dentro la promessa dell'operazione (e.promise): si rilegge il carrello solo
       quando quella e' finita, se no il pannello si ridisegnava vuoto. */
    offs.push(on(document, 'shopify:cart:lines-update', function (e) {
      var fromQuickAdd = e.target && e.target.closest && e.target.closest('product-form-component, quick-add-component');
      var done = e.promise && typeof e.promise.then === 'function'
        ? Promise.resolve(e.promise).catch(function () {})
        : new Promise(function (resolve) { window.setTimeout(resolve, 1500); });
      dirty = true;
      if (fromQuickAdd && !busy) {
        setBusy(true);
        open();
        finish(done.then(function () { return refresh(); }).then(function () { return allinea(); }));
      } else {
        done.then(function () { if (!busy) return refresh(); }).then(riallinea).catch(function () {});
      }
    }));

    offs.push(on(document, 'visibilitychange', function () {
      if (!document.hidden) dirty = true;
    }));

    offs.push(on(document, 'click', function (e) {
      var opener = e.target.closest && e.target.closest('[data-mrd-cart-open]');
      if (!opener) return;
      e.preventDefault();
      open();
      if (dirty && !busy) {
        setBusy(true);
        finish(refresh().then(function () { return allinea(); }));
      }
    }));

    /* VERSO LA CASSA (08/10/2026, «deve essere più veloce… quando uno clicca per andare al
       checkout»). Misurato: la richiesta parte 17 ms dopo il tocco, il tema non la trattiene;
       il resto (prima scritta dopo ~0,5 s, pagina fatta dopo ~1,2 s) e' la pagina di Shopify,
       che dal tema non si tocca. Nell'attesa pero' non succedeva niente a schermo: ora il
       bottone risponde subito con una linea che avanza (classe is-going, stile nella sezione).
       La classe si toglie da sola dopo 8 s (rete caduta: il bottone deve tornare vivo) e
       quando si torna indietro dalla cassa (la pagina puo' tornare cosi' com'era stata lasciata). */
    var cassaTimer = 0;
    function versoLaCassa() {
      root.classList.add('is-going');
      window.clearTimeout(cassaTimer);
      cassaTimer = window.setTimeout(function () { root.classList.remove('is-going'); }, 8000);
    }
    offs.push(on(window, 'pageshow', function () {
      window.clearTimeout(cassaTimer);
      root.classList.remove('is-going');
    }));

    /* La cassa: se i conti del regalo non tornano (caso raro: di norma sono gia' a posto), prima
       si pareggiano e poi si va. Una volta sola: se non riesce, alla cassa si va comunque. */
    offs.push(on(root, 'submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('.mrd-cart__checkout-form')) return;
      /* Secondo tocco mentre la cassa sta gia' arrivando: rifarebbe partire la richiesta da capo. */
      if (root.classList.contains('is-going')) { e.preventDefault(); return; }
      /* Anche i gemelli (13/10/2026): alla cassa non si va con due orologi pieni o coi soli gemelli. */
      if (cassaProvata || (regaliPari() && !gemelliStorti())) { versoLaCassa(); return; }
      e.preventDefault();
      if (busy) return;
      cassaProvata = true;
      setBusy(true);
      finish(allinea()).then(function () {
        var bottone = root.querySelector('.mrd-cart__checkout-form [name="checkout"]');
        if (bottone) bottone.click();
      });
    }));

    offs.push(on(root, 'click', function (e) {
      if (e.target.closest('[data-mrd-cart-close]')) {
        close();
        return;
      }
      /* Le due frecce della fila «Il tuo prossimo orologio»: una scheda per volta (dal 13/10/2026,
         sera, la fila scorre di lato con una scheda larga quasi tutto il pannello; spazio 8px). */
      var arrow = e.target.closest('[data-mrd-cart-like-arrow]');
      if (arrow) {
        var track = root.querySelector('[data-mrd-cart-like-track]');
        var card = track && track.firstElementChild;
        if (card) {
          var step = card.getBoundingClientRect().width + 8;
          track.scrollBy({
            left: arrow.getAttribute('data-mrd-cart-like-arrow') === 'prev' ? -step : step,
            behavior: reduceMotion.matches ? 'auto' : 'smooth'
          });
        }
        return;
      }
      var button = e.target.closest('[data-mrd-cart-qty]');
      if (!button || busy || button.disabled) return;
      change(parseInt(button.getAttribute('data-mrd-cart-line'), 10), parseInt(button.getAttribute('data-mrd-cart-qty'), 10), button.closest('.mrd-cart__item'));
    }));

    offs.push(on(document, 'keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.slice.call(panel.querySelectorAll('a[href], button:not([disabled])'))
        .filter(function (el) { return el.offsetParent !== null && el.tabIndex !== -1; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }));

    /* Tornando indietro dalla cassa il browser puo' mostrare la pagina com'era: si riallinea. */
    offs.push(on(window, 'pageshow', function (e) {
      if (e.persisted) refresh().then(riallinea).catch(function () {});
    }));

    /* All'apertura della pagina: se nel carrello c'e' un prodotto ma non il regalo (carrello nato
       prima), o un regalo rimasto solo, si pareggia subito. Di norma non parte nessuna richiesta. */
    riallinea();

    return {
      open: open,
      destroy: function () {
        close(false);
        window.clearTimeout(hideTimer);
        window.clearInterval(timerId);
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Video della hero: un solo <video>, sorgente per telefono o per computer secondo la
     larghezza (cosi' non se ne scaricano due). Per chi ha chiesto meno movimento resta fermo
     sull'anteprima.
     --------------------------------------------------------------------------- */
  function createHeroVideo(video) {
    var mobile = video.getAttribute('data-src-mobile');
    var desktop = video.getAttribute('data-src-desktop');
    var mq = window.matchMedia('(min-width: 768px)');
    // Il browser puo' RIFIUTARE l'avvio automatico anche con muted+playsinline: iPhone col
    // Risparmio energetico, i browser dentro le app (Instagram, Shopify), certe impostazioni
    // di Chrome. In quel caso resta il fotogramma fermo col tasto play di sistema. Allora si
    // riprova: al primo tocco o scorrimento della pagina (li' il permesso c'e' sempre),
    // quando la scheda torna in primo piano e quando il file ha abbastanza dati.
    var GESTI = ['touchstart', 'pointerdown', 'mousedown', 'keydown', 'scroll'];
    var inAttesa = false;
    // Sua scelta del 20/09/2026: «se il telefono lascia fare parte il video in loop, se ci sono
    // problemi allora c'e' solo una foto». La foto e' il fondo del riquadro (--mrd-hero-foto,
    // scritto da mrd-hero.liquid). Senza foto si tiene il ripiego vecchio: si riprova al primo
    // gesto dell'utente.
    var media = video.parentElement;
    var haFoto = !!(media && media.hasAttribute('data-mrd-hero-foto'));
    var attesaMax = null;
    var spento = false;

    // Video in fondo alla pagina (data-mrd-lazy): se il telefono rifiuta l'avvio automatico
    // NON resta solo la foto ma compare il tasto play bianco sotto il titolo (13/10/2026: «se a
    // uno non parte perche' ha il risparmio energetico allora vede il tasto play»). Il tocco e'
    // un gesto vero, quindi li' play() e' sempre permesso. Il video in cima resta com'era.
    var lazy = video.hasAttribute('data-mrd-lazy');
    var sezione = video.closest('.mrd-hero');
    var tasto = lazy && sezione ? sezione.querySelector('[data-mrd-hero-play]') : null;

    function mostraTasto() {
      if (!tasto || spento) return;
      clearTimeout(attesaMax);
      tasto.hidden = false;
    }

    function nascondiTasto() {
      if (tasto) tasto.hidden = true;
    }

    function suTasto() {
      nascondiTasto();
      video.muted = true;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function (err) {
          if (err && err.name === 'AbortError') return;
          mostraTasto();
        });
      }
    }
    if (tasto) tasto.addEventListener('click', suTasto);

    function restaLaFoto() {
      if (spento || !haFoto) return;
      spento = true;
      clearTimeout(attesaMax);
      smettiDiAspettare();
      nascondiTasto();
      try { video.pause(); } catch (e) {}
      video.removeAttribute('autoplay');
      video.removeAttribute('src');
      try { video.load(); } catch (e) {}   // ferma anche lo scaricamento: niente dati sprecati
      media.classList.remove('is-video');
      video.hidden = true;                 // il video non c'e' proprio piu': nessun tasto play
    }

    // Il video si SCOPRE solo quando sta davvero andando: fino a quel momento si vede la foto
    // (il video e' sopra ma trasparente, vedi il CSS di mrd-hero.liquid).
    function scopriIlVideo() {
      if (spento || !haFoto) return;
      if (!media.classList.contains('is-video')) media.classList.add('is-video');
    }

    function provaAPartire() {
      // Sua richiesta (20/09/2026): il video della hero deve partire sempre da solo, anche con
      // «Riduci movimento» acceso. E' muto, dura 5 secondi e non ha comandi: resta decorativo.
      if (spento || !acceso) return;   // senza src play() fallisce: sarebbe «non parte»
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function (err) {
          if (tasto) {
            // AbortError = play() interrotto da un cambio di sorgente: non e' il telefono che
            // blocca, si riprova da soli (canplay). Il tasto solo per il rifiuto vero.
            if (err && err.name === 'AbortError') return;
            mostraTasto();
            return;
          }
          if (haFoto) restaLaFoto(); else aspettaUnGesto();
        });
      }
    }

    function riprova() {
      if (video.paused) provaAPartire();
      if (!video.paused) smettiDiAspettare();
    }

    function aspettaUnGesto() {
      if (inAttesa) return;
      inAttesa = true;
      GESTI.forEach(function (nome) {
        document.addEventListener(nome, riprova, { capture: true, passive: true });
      });
    }

    function smettiDiAspettare() {
      if (!inAttesa) return;
      inAttesa = false;
      GESTI.forEach(function (nome) {
        document.removeEventListener(nome, riprova, true);
      });
    }

    // Video in fondo alla pagina (data-mrd-lazy, vedi mrd-hero.liquid): senza src finche' non
    // si arriva vicino; prima di allora non si cambia sorgente e non si prova a partire.
    // (`lazy` e' letto piu' su, col tasto play.)
    var acceso = !lazy;

    function apply() {
      if (spento || !acceso) return;
      var want = mq.matches ? (desktop || mobile) : (mobile || desktop);
      if (want && video.getAttribute('src') !== want) {
        video.setAttribute('src', want);
        video.load();
      }
      provaAPartire();
    }

    // Se dopo 5 secondi non si e' mosso di un fotogramma (rete lenta, file che non parte),
    // vale come «ci sono problemi»: resta la foto.
    function controllaSeSiMuove() {
      if (!haFoto) return;
      attesaMax = setTimeout(function () {
        // Col tasto: un video mai partito (fermo) e' «bloccato» anche se il telefono non l'ha
        // detto -> tasto play. Uno partito ma fermo a caricare -> resta la foto, come prima.
        if (tasto && video.paused) { mostraTasto(); return; }
        if (video.paused || video.currentTime < 0.2) restaLaFoto();
      }, 5000);
    }

    // «Sta andando» non e' `paused = false`: su rete lenta il browser dice di aver avviato e
    // poi resta fermo a caricare. Conta solo se il tempo del video avanza davvero.
    function vaBene() {
      if (video.currentTime > 0.2) {
        clearTimeout(attesaMax);
        smettiDiAspettare();
        nascondiTasto();
        scopriIlVideo();
      }
    }

    var guarda = null;
    function accendi() {
      if (acceso) return;
      acceso = true;
      if (guarda) { guarda.disconnect(); guarda = null; }
      video.muted = true;
      video.autoplay = true;   // cosi' il browser lo riprende da solo se lo ferma fuori schermo
      apply();
      controllaSeSiMuove();
    }

    if (lazy && 'IntersectionObserver' in window) {
      // Mezzo schermo d'anticipo: quando la sezione arriva a vista il video sta gia' andando, e
      // all'apertura della scheda (sezione a ~1600px da telefono) non ruba banda alle foto.
      guarda = new IntersectionObserver(function (voci) {
        if (voci.some(function (v) { return v.isIntersecting; })) accendi();
      }, { rootMargin: '50% 0px' });
      guarda.observe(media || video);
    } else if (lazy) {
      accendi();
    } else {
      apply();
      controllaSeSiMuove();
    }
    mq.addEventListener('change', apply);
    video.addEventListener('canplay', riprova);
    video.addEventListener('loadeddata', riprova);
    video.addEventListener('playing', vaBene);
    video.addEventListener('timeupdate', vaBene);
    video.addEventListener('error', restaLaFoto);
    document.addEventListener('visibilitychange', riprova);
    return {
      destroy: function () {
        smettiDiAspettare();
        clearTimeout(attesaMax);
        if (guarda) guarda.disconnect();
        if (tasto) tasto.removeEventListener('click', suTasto);
        mq.removeEventListener('change', apply);
        video.removeEventListener('canplay', riprova);
        video.removeEventListener('loadeddata', riprova);
        video.removeEventListener('playing', vaBene);
        video.removeEventListener('timeupdate', vaBene);
        video.removeEventListener('error', restaLaFoto);
        document.removeEventListener('visibilitychange', riprova);
      }
    };
  }

  /* ---------------------------------------------------------------------------
     «Tira per aggiornare»: quando la pagina e' GIA' in cima e il dito scende, il telefono
     ricarica la pagina e riparte dall'alto. Gli capita scorrendo le foto della scheda
     prodotto (sua segnalazione, 18/09/2026). Il CSS `overscroll-behavior` basta su Android
     ma non su iPhone, quindi il gesto si ferma anche a mano, con tre condizioni perche' non
     rompa nient'altro: deve essere un dito solo, il gesto deve essere chiaramente verticale
     verso il basso (se no si romperebbe lo scorrimento laterale della galleria), e sotto il
     dito non ci deve essere niente che possa ancora scorrere (carrello, liste).
     --------------------------------------------------------------------------- */
  function bloccaTiraPerAggiornare() {
    var startX = 0;
    var startY = 0;
    var singolo = false;

    function puoAncoraSalire(node) {
      for (var el = node; el && el.nodeType === 1 && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        if (el.scrollTop > 0 && el.scrollHeight - el.clientHeight > 2) {
          var overflow = window.getComputedStyle(el).overflowY;
          if (overflow === 'auto' || overflow === 'scroll') return true;
        }
      }
      return false;
    }

    document.addEventListener('touchstart', function (e) {
      singolo = e.touches.length === 1;
      if (!singolo) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      if (!singolo || e.touches.length !== 1 || !e.cancelable) return;
      if (window.scrollY > 0) return;
      var dy = e.touches[0].clientY - startY;
      var dx = Math.abs(e.touches[0].clientX - startX);
      if (dy < 8 || dy < dx * 1.2) return;
      if (puoAncoraSalire(e.target)) return;
      e.preventDefault();
    }, { passive: false });
  }

  /* ---------------------------------------------------------------------------
     Recensioni

     Tre comandi, tutti sullo stesso elenco: l'interruttore del dettaglio per
     stella, le barre che filtrano, il tasto che ne mostra altre. Il filtro e il
     «mostra altre» lavorano insieme: cambiando stella si riparte dal primo
     gruppo, se no si aprirebbe un elenco gia' scorso a meta'.
     --------------------------------------------------------------------------- */
  function createReviews(root) {
    var apriDettagli = root.querySelector('[data-mrd-rv-dettagli-apri]');
    var dettagli = root.querySelector('[data-mrd-rv-dettagli]');
    var barre = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-rv-filtro]'));
    var voci = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-rv-item]'));
    var altre = root.querySelector('[data-mrd-rv-altre]');
    var apriModulo = root.querySelector('[data-mrd-rv-apri]');
    var modulo = root.querySelector('[data-mrd-rv-form]');

    var passo = parseInt(root.getAttribute('data-mrd-rv-passo'), 10) || 5;
    var mostrate = passo;
    var filtro = 0;
    var via = [];

    function disegna() {
      var viste = 0;
      for (var i = 0; i < voci.length; i++) {
        var v = voci[i];
        // 16/10/2026: senza filtro solo 5 e 4 stelle; le 3 si vedono toccando la loro barra.
        var st = parseInt(v.getAttribute('data-stelle'), 10);
        var suo = filtro === 0 ? st >= 4 : st === filtro;
        if (suo && viste < mostrate) {
          v.hidden = false;
          viste++;
        } else {
          v.hidden = true;
          if (suo) viste++;
        }
      }
      if (altre) altre.hidden = viste <= mostrate;
    }

    function filtra(stelle) {
      filtro = filtro === stelle ? 0 : stelle;
      mostrate = passo;
      for (var i = 0; i < barre.length; i++) {
        var n = parseInt(barre[i].getAttribute('data-mrd-rv-filtro'), 10);
        barre[i].setAttribute('aria-pressed', n === filtro ? 'true' : 'false');
      }
      disegna();
    }

    if (apriDettagli && dettagli) {
      via.push(on(apriDettagli, 'click', function () {
        var aperto = apriDettagli.getAttribute('aria-expanded') === 'true';
        apriDettagli.setAttribute('aria-expanded', aperto ? 'false' : 'true');
        dettagli.hidden = aperto;
        var testo = apriDettagli.querySelector('[data-mrd-rv-dettagli-testo]');
        if (testo) {
          testo.textContent = aperto
            ? apriDettagli.getAttribute('data-chiuso')
            : apriDettagli.getAttribute('data-aperto');
        }
      }));
    }

    barre.forEach(function (b) {
      b.setAttribute('aria-pressed', 'false');
      if (b.disabled) return;
      via.push(on(b, 'click', function () {
        filtra(parseInt(b.getAttribute('data-mrd-rv-filtro'), 10));
      }));
    });

    if (altre) {
      via.push(on(altre, 'click', function () {
        mostrate += passo;
        disegna();
      }));
    }

    if (apriModulo && modulo) {
      via.push(on(apriModulo, 'click', function () {
        modulo.hidden = !modulo.hidden;
        if (!modulo.hidden) {
          var primo = modulo.querySelector('input, select, textarea');
          if (primo) primo.focus();
        }
      }));
    }

    via.push.apply(via, zoomFoto(root));

    disegna();

    return {
      destroy: function () { via.forEach(function (f) { f(); }); }
    };
  }

  /* La foto del cliente, grande (14/10/2026). Nella pagina c'e' solo la miniatura: la foto
     grande parte al tocco (srcset 800-1600, il telefono sceglie). Una finestra sola per
     sezione; si chiude con la X, toccando fuori dalla foto o con Esc. Vale per l'elenco in
     fondo alla scheda (createReviews) e, dal 15/10/2026, per la fila «Recensioni in fila»
     (mrd-voices, data-mrd-zoom). Risponde con le funzioni che staccano gli ascolti. */
  function zoomFoto(root) {
    var via = [];
    var finestra = root.querySelector('[data-mrd-rv-zoom-finestra]');
    var fotoGrande = finestra && finestra.querySelector('[data-mrd-rv-zoom-foto]');
    if (!finestra || !fotoGrande || typeof finestra.showModal !== 'function') return via;
    via.push(on(root, 'click', function (e) {
      var mini = e.target.closest && e.target.closest('[data-mrd-rv-zoom]');
      if (!mini || !root.contains(mini)) return;
      var img = mini.querySelector('img');
      fotoGrande.alt = img ? img.alt : '';
      fotoGrande.sizes = '(min-width: 720px) 720px, 100vw';
      fotoGrande.srcset = mini.getAttribute('data-srcset') || '';
      fotoGrande.src = mini.getAttribute('data-src') || '';
      finestra.showModal();
    }));
    via.push(on(finestra, 'click', function (e) {
      if (e.target === fotoGrande) return;
      finestra.close();
    }));
    return via;
  }

  function createZoom(root) {
    var via = zoomFoto(root);
    return {
      destroy: function () { via.forEach(function (f) { f(); }); }
    };
  }

  /* ---------------------------------------------------------------------------
     La guida del bracciale in una finestra (15/10/2026: «non voglio che escano dalla pagina
     di acquisto»). La finestra e' in mrd-product (data-mrd-guida); qui si prendono i tocchi
     su OGNI link della pagina che porta a /pages/come-regolare-il-bracciale (anche /en/...,
     /it-eu/...): sotto le misure, nei Dettagli, nelle domande frequenti.
     VELOCITA': la pagina della guida si scarica solo quando serve. Parte gia' quando il dito
     (o il mouse) arriva sul link, cosi' al tocco di solito e' pronta; poi resta in memoria e
     le altre aperture sono subito. Delle istruzioni si prende solo .mrd-tut__inner; le
     illustrazioni (in attesa, come in tutto il sito) partono quando la finestra si apre.
     Se lo scaricamento non riesce si va alla pagina, come prima.
     --------------------------------------------------------------------------- */
  var GUIDA_BRACCIALE = /\/pages\/come-regolare-il-bracciale\/?$/;

  function createGuida(root) {
    var corpo = root.querySelector('[data-mrd-guida-corpo]');
    var testo = root.querySelector('[data-mrd-guida-testo]');
    /* chiudono: «Torna indietro» in alto, la X, «Torna indietro» in fondo */
    var chiudi = root.querySelectorAll('[data-mrd-guida-chiudi]');
    if (!corpo || !testo || typeof root.showModal !== 'function') return { destroy: function () {} };
    var html = document.documentElement;
    var via = [];
    var carica = null;
    var messa = false;
    var bloccata = false;

    function linkGuida(t) {
      var a = t && t.closest && t.closest('a[href]');
      if (!a || root.contains(a)) return null;
      var u;
      try { u = new URL(a.href, window.location.href); } catch (e) { return null; }
      if (u.origin !== window.location.origin || !GUIDA_BRACCIALE.test(u.pathname)) return null;
      return a;
    }

    function scarica(href) {
      if (!carica) {
        carica = fetch(href, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('guida ' + r.status);
          return r.text();
        }).then(function (testo) {
          var dentro = new DOMParser().parseFromString(testo, 'text/html').querySelector('.mrd-tut__inner');
          if (!dentro) throw new Error('guida vuota');
          return dentro;
        });
        carica.catch(function () { carica = null; });
      }
      return carica;
    }

    function metti(dentro) {
      if (messa) return;
      messa = true;
      var pezzo = document.importNode(dentro, true);
      /* Il titolo della pagina e' un h1: nella scheda l'h1 e' il nome dell'orologio. */
      var titolo = pezzo.querySelector('h1');
      if (titolo) {
        var h2 = document.createElement('h2');
        h2.className = titolo.className;
        h2.id = 'mrd-guida-titolo';
        h2.innerHTML = titolo.innerHTML;
        titolo.parentNode.replaceChild(h2, titolo);
        root.removeAttribute('aria-label');
        root.setAttribute('aria-labelledby', h2.id);
      }
      Array.prototype.forEach.call(pezzo.querySelectorAll('noscript'), function (n) { n.remove(); });
      Array.prototype.forEach.call(pezzo.querySelectorAll('img[data-mrd-defer]'), function (img) {
        if (img.getAttribute('data-mrd-srcset')) img.setAttribute('srcset', img.getAttribute('data-mrd-srcset'));
        if (img.getAttribute('data-mrd-src')) img.setAttribute('src', img.getAttribute('data-mrd-src'));
        img.removeAttribute('data-mrd-defer');
        img.setAttribute('loading', 'eager');
      });
      var sezione = document.createElement('div');
      sezione.className = 'mrd-tut';
      sezione.appendChild(pezzo);
      testo.innerHTML = '';
      testo.appendChild(sezione);
      tieniLingua(testo);
    }

    function apri(a) {
      if (!messa) root.setAttribute('aria-label', a.textContent.trim());
      root.showModal();
      corpo.scrollTop = 0;
      if (!html.hasAttribute('scroll-lock')) {
        html.setAttribute('scroll-lock', '');
        bloccata = true;
      }
      if (messa) return;
      scarica(a.href).then(metti, function () {
        root.close();
        window.location.href = a.href;
      });
    }

    function presto(e) {
      var a = linkGuida(e.target);
      if (a) scarica(a.href);
    }

    via.push(on(document, 'click', function (e) {
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = linkGuida(e.target);
      if (!a) return;
      e.preventDefault();
      apri(a);
    }));
    via.push(on(document, 'pointerover', presto));
    via.push(on(document, 'touchstart', presto, { passive: true }));
    via.push(on(document, 'focusin', presto));
    Array.prototype.forEach.call(chiudi, function (b) {
      via.push(on(b, 'click', function () { root.close(); }));
    });
    /* Un tocco fuori dal riquadro (sul velo scuro) chiude: lo riceve la finestra stessa. */
    via.push(on(root, 'click', function (e) {
      if (e.target === root) root.close();
    }));
    via.push(on(root, 'close', function () {
      if (bloccata) {
        html.removeAttribute('scroll-lock');
        bloccata = false;
      }
    }));

    return {
      destroy: function () {
        via.forEach(function (f) { f(); });
        if (root.open) root.close();
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Domande frequenti: una alla volta

     L'HTML lo sa fare da solo con <details name="...">, ma i telefoni piu' vecchi
     (iPhone fermi a iOS 16, Android datati) quell'attributo lo ignorano e restano
     tutte aperte. Qui il ripiego: quando se ne apre una, le sorelle con lo stesso
     name si chiudono. Costa un ascoltatore e vale su tutti i telefoni.
     --------------------------------------------------------------------------- */
  function unaAllaVolta() {
    if ('name' in document.createElement('details')) return;
    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || d.tagName !== 'DETAILS' || !d.open) return;
      var nome = d.getAttribute('name');
      if (!nome) return;
      var tutte = document.querySelectorAll('details[name="' + nome.replace(/"/g, '\\"') + '"]');
      for (var i = 0; i < tutte.length; i++) {
        if (tutte[i] !== d && tutte[i].open) tutte[i].open = false;
      }
    }, true);
  }

  /* ---------------------------------------------------------------------------
     Fisarmoniche: il titolo che tocchi resta dov'e'

     Sua segnalazione del 20/09/2026, dal telefono: se e' aperta una domanda LUNGA e se ne apre
     un'altra piu' in basso, quella lunga si chiude, la pagina si accorcia di colpo e il titolo
     appena toccato scappa in alto — ci si ritrova in un'altra sezione. Qui si misura dov'era il
     titolo al momento del tocco e lo si tiene fermo per tutta la durata della chiusura
     (l'altezza si chiude con una transizione, quindi non basta correggere una volta sola).
     Se nel frattempo la persona scorre da sola, si molla subito: comanda lei.
     --------------------------------------------------------------------------- */
  function tieniFermoIlTitolo() {
    var DURATA = 700;   // ms, piu' della transizione piu' lunga delle fisarmoniche

    document.addEventListener('click', function (e) {
      var nodo = e.target;
      var sommario = nodo && nodo.closest ? nodo.closest('summary') : null;
      if (!sommario) return;
      var det = sommario.parentElement;
      if (!det || det.tagName !== 'DETAILS' || !det.hasAttribute('name')) return;

      var partenza = sommario.getBoundingClientRect().top;
      var inizio = null;
      var mollato = false;

      function molla() { mollato = true; }
      var GESTI = ['touchstart', 'wheel', 'keydown'];
      GESTI.forEach(function (g) { window.addEventListener(g, molla, { passive: true }); });

      function smetti() {
        GESTI.forEach(function (g) { window.removeEventListener(g, molla); });
      }

      function insegui(t) {
        if (mollato) { smetti(); return; }
        if (inizio === null) inizio = t;
        var scarto = sommario.getBoundingClientRect().top - partenza;
        if (Math.abs(scarto) > 0.5) window.scrollBy(0, scarto);
        if (t - inizio < DURATA) { requestAnimationFrame(insegui); } else { smetti(); }
      }

      requestAnimationFrame(insegui);
    }, true);
  }

  /* ---------------------------------------------------------------------------
     Video «dai social» [data-mrd-reels] (Meridiano, 04/10/2026): riquadri piccoli col tasto
     play al centro, come nel riferimento. NON partono da soli: si tocca il riquadro e parte
     quel video con l'audio (c'e' il gesto, quindi il telefono lo lascia fare); toccandolo di
     nuovo si ferma; facendone partire un altro, il primo si ferma. Il file si scarica solo
     al primo tocco (preload="none"): la pagina non pesa finche' nessuno guarda.
     --------------------------------------------------------------------------- */
  function createReels(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-reel]'));
    if (!items.length) return null;
    var offs = [];

    function videoOf(item) {
      return item.querySelector('video');
    }

    function stop(item) {
      var video = videoOf(item);
      if (video && !video.paused) video.pause();
      item.classList.remove('is-playing');
    }

    items.forEach(function (item) {
      var video = videoOf(item);
      if (!video) return;
      offs.push(on(item, 'click', function () {
        if (!video.paused) { stop(item); return; }
        items.forEach(function (other) { if (other !== item) stop(other); });
        video.muted = false;
        var promise = video.play();
        item.classList.add('is-playing');
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () { item.classList.remove('is-playing'); });
        }
      }));
      offs.push(on(video, 'pause', function () { item.classList.remove('is-playing'); }));
    });

    /* Uscito dallo schermo, il video si ferma. */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) stop(entry.target);
      });
    }, { threshold: 0.2 });
    items.forEach(function (item) { io.observe(item); });

    return {
      destroy: function () {
        io.disconnect();
        items.forEach(stop);
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     «Carica altro» [data-mrd-more]: gli elementi [data-mrd-more-item] oltre i primi nascono
     nascosti; il tasto [data-mrd-more-button] ne scopre data-step per volta e, finiti, sparisce.
     --------------------------------------------------------------------------- */
  function createMore(root) {
    var button = root.querySelector('[data-mrd-more-button]');
    if (!button) return null;
    var step = parseInt(root.getAttribute('data-step'), 10) || 4;

    function hiddenItems() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-mrd-more-item][hidden]'));
    }

    function paint() {
      button.hidden = hiddenItems().length === 0;
    }

    var off = on(button, 'click', function () {
      hiddenItems().slice(0, step).forEach(function (item) { item.hidden = false; });
      paint();
    });
    paint();

    return { destroy: off };
  }

  /* ---------------------------------------------------------------------------
     Pagina di collezione [data-mrd-collection] (sections/mrd-collection.liquid).
     - [data-mrd-filters-open] apre e chiude il pannello [data-mrd-filters-panel]; Esc lo chiude.
     - [data-mrd-sort] e' il <select> dell'ordinamento: al cambio si ricarica la pagina con
       `sort_by` nell'indirizzo, tenendo i filtri accesi e tornando alla prima pagina.
     - Il modulo [data-mrd-filters-form] parte da solo (GET): qui si spengono soltanto i campi
       del prezzo lasciati vuoti, che se no finiscono nell'indirizzo come filtri a vuoto. Al
       ritorno con «indietro» il browser li ricorda spenti: `pageshow` li riaccende.
     --------------------------------------------------------------------------- */
  function createCollection(root) {
    var opener = root.querySelector('[data-mrd-filters-open]');
    var panel = root.querySelector('[data-mrd-filters-panel]');
    var sort = root.querySelector('[data-mrd-sort]');
    var form = root.querySelector('[data-mrd-filters-form]');
    var offs = [];

    function toggle(open) {
      panel.hidden = !open;
      opener.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (opener && panel) {
      offs.push(on(opener, 'click', function () { toggle(panel.hidden); }));
      offs.push(on(root, 'keydown', function (e) {
        if (e.key !== 'Escape' || panel.hidden) return;
        toggle(false);
        opener.focus();
      }));
    }

    if (sort) {
      offs.push(on(sort, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        url.hash = '';
        window.location.assign(url.toString());
      }));
    }

    if (form) {
      var prices = Array.prototype.slice.call(form.querySelectorAll('input[type="number"]'));
      offs.push(on(form, 'submit', function () {
        prices.forEach(function (input) {
          /* Vuoto, oppure «da 0»: non e' un filtro, e nell'indirizzo conterebbe come acceso. */
          var zeroMin = /\.gte$/.test(input.name) && Number(input.value) === 0;
          if (input.value === '' || zeroMin) input.disabled = true;
        });
      }));
      offs.push(on(window, 'pageshow', function () {
        prices.forEach(function (input) { input.disabled = false; });
      }));
    }

    return { destroy: function () { offs.forEach(function (off) { off(); }); } };
  }

  /* ---------------------------------------------------------------------------
     Footer [data-mrd-footer] (sections/mrd-footer.liquid): le voci [data-mrd-footer-group]
     sono <details>. Da telefono fisarmoniche chiuse; da 1024px in su colonne sempre aperte,
     e il titolo non si puo' richiudere. Cambiando larghezza si riallineano.
     --------------------------------------------------------------------------- */
  function createFooter(root) {
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-footer-group]'));
    if (!groups.length || !window.matchMedia) return null;
    var wide = window.matchMedia('(min-width: 1024px)');
    var offs = [];

    function apply() {
      groups.forEach(function (group) { group.open = wide.matches; });
    }

    apply();
    if (wide.addEventListener) offs.push(on(wide, 'change', apply));
    offs.push(on(root, 'click', function (e) {
      if (wide.matches && e.target.closest && e.target.closest('summary')) e.preventDefault();
    }));

    return { destroy: function () { offs.forEach(function (off) { off(); }); } };
  }

  /* ---------------------------------------------------------------------------
     «Spedizione entro lunedi' 5 ottobre» [data-mrd-ship-date]: oggi + data-days giorni
     LAVORATIVI (sabato e domenica non contano), scritta in italiano. Si calcola nel browser
     e non in Liquid: le pagine di Shopify restano in memoria per ore e la data resterebbe
     vecchia. Il contenitore [data-mrd-ship] nasce nascosto e si scopre a data scritta.
     LA PROMESSA DEVE ESSERE VERA: i giorni sono un'impostazione, li decide lui.
     --------------------------------------------------------------------------- */
  function shipDates(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mrd-ship-date]'), function (el) {
      var days = parseInt(el.getAttribute('data-days'), 10);
      if (isNaN(days) || days < 0) return;
      var date = new Date();
      var added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) added++;
      }
      while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
      var text;
      try {
        text = date.toLocaleDateString(LINGUA_DATE, { weekday: 'long', day: 'numeric', month: 'long' });
      } catch (err) {
        text = date.getDate() + '/' + (date.getMonth() + 1);
      }
      el.textContent = text;
      var box = el.closest('[data-mrd-ship]');
      if (box) box.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------------
     Scelta della quantita' sulla scheda prodotto [data-mrd-bundle] (Meridiano, 04/10/2026).
     Ogni riga [data-mrd-bundle-card] e' un'offerta: «1 orologio», «2 orologi»... (data-qty sul
     suo radio [data-mrd-bundle-option]). Le offerte da 2 in su hanno un menu per pezzo
     (select[data-mrd-piece]) per scegliere colori diversi.
     - Il primo pezzo segue il colore scelto in alto; gli altri lo seguono finche' non li si
       tocca. Cambiando il primo pezzo cambia anche il colore in alto (foto, prezzo).
     - I pezzi da mandare al carrello si scrivono in [data-mrd-bundle-items] come items[n][id]
       e items[n][quantity]: il carrello li aggiunge tutti con una richiesta sola.
     - I totali qui sono SOLO a video (somma dei prezzi veri delle varianti scelte): nel
       carrello prezzi e sconti li scrive sempre Shopify. Nessuno sconto si calcola qui.
     --------------------------------------------------------------------------- */
  function createBundle(root) {
    var product = root.closest('[data-mrd-product]');
    if (!product) return null;
    var data = product.querySelector('script[data-mrd-variants]');
    var box = product.querySelector('[data-mrd-bundle-items]');
    var options = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-bundle-option]'));
    if (!data || !box || !options.length) return null;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (err) { return null; }
    var byId = {};
    variants.forEach(function (v) { byId[String(v.id)] = v; });

    var keeper = product.querySelector('input[data-mrd-variant-id]');
    var groups = Array.prototype.slice.call(product.querySelectorAll('[data-mrd-option]'));
    var stock = product.querySelector('[data-mrd-stock]');
    var offs = [];

    /* «€ 1.234,56»: lo stesso aspetto dei prezzi scritti da Liquid nella scheda. In un'altra
       moneta (mercati del Regno Unito, della Svizzera… dal 12/10/2026) la scrive il browser. */
    function money(cents) {
      var moneta = window.Shopify && Shopify.currency && Shopify.currency.active;
      if (moneta && moneta !== 'EUR') {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: moneta }).format(cents / 100);
        } catch (err) { /* sotto, come sempre */ }
      }
      var n = Math.round(cents);
      var whole = String(Math.floor(n / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return '€ ' + whole + ',' + ('0' + (n % 100)).slice(-2);
    }

    function cardOf(option) {
      return option.closest('[data-mrd-bundle-card]');
    }

    function piecesOf(option) {
      return Array.prototype.slice.call(cardOf(option).querySelectorAll('select[data-mrd-piece]'));
    }

    function currentId() {
      return keeper ? String(keeper.value) : String(variants[0].id);
    }

    function itemsOf(option) {
      var qty = parseInt(option.getAttribute('data-qty'), 10) || 1;
      var pieces = piecesOf(option);
      if (!pieces.length) return [{ id: currentId(), quantity: qty }];
      return pieces.map(function (select) { return { id: String(select.value), quantity: 1 }; });
    }

    /* data-paid = quanti pezzi si pagano (di partenza tutti). Se e' minore dei pezzi («compri 1,
       ricevi 1») si pagano i piu' cari e gli altri sono in omaggio: VALE SOLO se in Shopify c'e'
       lo sconto automatico che fa la stessa cosa, se no il carrello dira' un altro prezzo. */
    function totals(option) {
      var prices = [];
      var compare = 0;
      itemsOf(option).forEach(function (item) {
        var v = byId[item.id];
        if (!v) return;
        for (var k = 0; k < item.quantity; k++) {
          prices.push(v.cents);
          compare += v.compare_cents > v.cents ? v.compare_cents : v.cents;
        }
      });
      prices.sort(function (a, b) { return b - a; });
      var paid = parseInt(option.getAttribute('data-paid'), 10);
      if (!paid || paid > prices.length) paid = prices.length;
      var price = 0;
      for (var i = 0; i < paid; i++) price += prices[i];
      return { price: price, compare: compare };
    }

    function selected() {
      for (var i = 0; i < options.length; i++) {
        if (options[i].checked) return options[i];
      }
      return options[0];
    }

    function paint() {
      options.forEach(function (option) {
        var card = cardOf(option);
        var t = totals(option);
        var price = card.querySelector('[data-mrd-bundle-price]');
        var compare = card.querySelector('[data-mrd-bundle-compare]');
        if (price) price.textContent = money(t.price);
        if (compare) {
          compare.textContent = t.compare > t.price ? money(t.compare) : '';
          compare.hidden = !(t.compare > t.price);
        }
        card.classList.toggle('is-selected', option.checked);

        /* Il pallino accanto a ogni menu prende il colore della variante scelta li'. */
        piecesOf(option).forEach(function (select) {
          var dot = select.parentElement.querySelector('[data-mrd-piece-dot]');
          var chosen = byId[String(select.value)];
          if (!dot) return;
          dot.hidden = !(chosen && chosen.dot);
          if (chosen && chosen.dot) dot.style.setProperty('--mrd-swatch', chosen.dot);
        });
      });

      var choice = selected();
      var total = totals(choice);
      Array.prototype.forEach.call(document.querySelectorAll('[data-mrd-total]'), function (el) {
        el.textContent = money(total.price);
      });
      Array.prototype.forEach.call(document.querySelectorAll('[data-mrd-total-compare]'), function (el) {
        el.textContent = total.compare > total.price ? money(total.compare) : '';
        el.hidden = !(total.compare > total.price);
      });

      while (box.firstChild) box.removeChild(box.firstChild);
      itemsOf(choice).forEach(function (item, i) {
        [['id', item.id], ['quantity', item.quantity]].forEach(function (pair) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[' + i + '][' + pair[0] + ']';
          input.value = pair[1];
          box.appendChild(input);
        });
      });

      /* Riga del magazzino. Se la variante e' tracciata e i pezzi sono pochi: «Ultimi N
         disponibili» col numero vero. Se no: la frase fissa scritta nelle impostazioni
         (data-fallback), se c'e'; vuota = riga nascosta. */
      if (stock) {
        var v = byId[currentId()];
        var limit = parseInt(stock.getAttribute('data-threshold'), 10) || 0;
        var few = !!v && typeof v.inv === 'number' && v.inv > 0 && v.inv <= limit;
        var fallback = stock.getAttribute('data-fallback') || '';
        var label = stock.querySelector('[data-mrd-stock-text]');
        stock.hidden = !few && !fallback;
        if (label) label.textContent = few ? (stock.getAttribute('data-text') || '').replace('[n]', v.inv) : fallback;
      }
    }

    /* Il colore scelto in alto porta con se' il primo pezzo, e gli altri finche' non li si tocca. */
    function follow() {
      var id = currentId();
      if (!byId[id]) return;
      options.forEach(function (option) {
        piecesOf(option).forEach(function (select, i) {
          if (i === 0 || !select.hasAttribute('data-touched')) select.value = id;
        });
      });
    }

    /* Il primo pezzo cambiato a mano porta con se' il colore in alto. */
    function lead(select) {
      var v = byId[String(select.value)];
      if (!v || !groups.length) return;
      var changed = null;
      groups.forEach(function (group, j) {
        Array.prototype.forEach.call(group.querySelectorAll('input[type="radio"]'), function (input) {
          if (input.value === v.options[j] && !input.checked) {
            input.checked = true;
            changed = group;
          }
        });
      });
      if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
    }

    offs.push(on(product, 'change', function (e) {
      var target = e.target;
      if (target && target.matches && target.matches('select[data-mrd-piece]')) {
        target.setAttribute('data-touched', '');
        var card = target.closest('[data-mrd-bundle-card]');
        var first = card && card.querySelector('select[data-mrd-piece]');
        if (first === target) lead(target);
      } else if (target && target.closest && target.closest('[data-mrd-option]')) {
        follow();
      }
      paint();
    }));

    follow();
    paint();

    return {
      destroy: function () {
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Domande frequenti a categorie (sections/mrd-help.liquid, 04/10/2026)
     I bottoni [data-mrd-help-tab] mostrano una categoria per volta [data-mrd-help-panel].
     L'indirizzo con ?tab=nome apre quella categoria, e a ogni cambio l'indirizzo si aggiorna
     (senza ricaricare e senza toccare gli altri parametri, come ?preview_theme_id).
     --------------------------------------------------------------------------- */
  function createHelp(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-help-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-mrd-help-panel]'));
    if (!tabs.length || panels.length < 2) return null;
    var offs = [];

    function has(key) {
      return panels.some(function (panel) { return panel.getAttribute('data-mrd-help-panel') === key; });
    }

    function select(key, write) {
      if (!has(key)) return;
      tabs.forEach(function (tab) {
        var active = tab.getAttribute('data-mrd-help-tab') === key;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.getAttribute('data-mrd-help-panel') === key);
      });
      if (!write) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('tab', key);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {}
    }

    tabs.forEach(function (tab, k) {
      offs.push(on(tab, 'click', function () { select(tab.getAttribute('data-mrd-help-tab'), true); }));
      /* Frecce destra e sinistra, come si aspetta chi usa la tastiera su una fila di tab. */
      offs.push(on(tab, 'keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var next = tabs[(k + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        e.preventDefault();
        select(next.getAttribute('data-mrd-help-tab'), true);
        next.focus();
      }));
    });

    var wanted = null;
    try { wanted = new URL(window.location.href).searchParams.get('tab'); } catch (e) {}
    select(wanted && has(wanted) ? wanted : tabs[0].getAttribute('data-mrd-help-tab'), false);

    return {
      show: function (element) {
        for (var i = 0; i < panels.length; i++) {
          if (panels[i] === element || panels[i].contains(element)) {
            select(panels[i].getAttribute('data-mrd-help-panel'), false);
            return;
          }
        }
      },
      destroy: function () {
        offs.forEach(function (off) { off(); });
      }
    };
  }

  /* ---------------------------------------------------------------------------
     Card prodotto: scelta del colore (snippets/mrd-product-card.liquid)
     Con la prova accesa (Impostazioni tema > Meridiano) le foto piccole della card si toccano
     (restano riquadri come prima, non tasti: sua correzione del 07/10/2026): toccandone una la
     card passa a quel colore - foto grande, nome del colore, prezzo, i link alla scheda e il
     colore che il tasto «Aggiungi» mette nel carrello. Il tasto e' un modulo verso /cart/add:
     lo prende il carrello a tendina (createCart) come ogni altro, regalo compreso.
     Un ascolto solo su tutta la pagina e non un modulo per card: le card arrivano anche dopo
     (filtri e «mostra altri» delle pagine elenco) e cosi' funzionano senza riaccenderle.
     --------------------------------------------------------------------------- */
  function scegliColore(button) {
    var card = button.closest('[data-mrd-card]');
    if (!card) return;
    var picks = Array.prototype.slice.call(card.querySelectorAll('[data-mrd-card-pick]'));
    picks.forEach(function (other) {
      other.classList.toggle('is-selected', other === button);
    });

    /* La foto grande. Se e' ancora «in attesa» (snippets/mrd-image.liquid) le si tolgono gli
       indirizzi in sospeso: se no lo script del layout, liberandola, rimetteva il primo colore. */
    var img = card.querySelector('.mrd-card__media > img:not(.mrd-card__alt)');
    var src = button.getAttribute('data-src');
    if (img && src) {
      var srcset = button.getAttribute('data-srcset');
      img.removeAttribute('data-mrd-defer');
      img.removeAttribute('data-mrd-srcset');
      img.removeAttribute('data-mrd-src');
      img.setAttribute('loading', 'eager');
      if (srcset) img.setAttribute('srcset', srcset);
      else img.removeAttribute('srcset');
      img.setAttribute('src', src);
      card.classList.add('is-picked');
    }

    /* Al primo tocco si scaricano anche le foto degli altri colori della card: chi ne guarda
       uno di solito li scorre tutti, e dal secondo in poi la foto cambia subito. Solo allora:
       prima sarebbero foto scaricate per niente su ogni card della pagina. */
    if (!card.__mrdColori) {
      card.__mrdColori = true;
      picks.forEach(function (other) {
        var altra = other.getAttribute('data-src');
        if (other === button || !altra) return;
        var pre = new Image();
        if (img && img.sizes) pre.sizes = img.sizes;
        if (other.getAttribute('data-srcset')) pre.srcset = other.getAttribute('data-srcset');
        pre.src = altra;
      });
    }

    var finish = card.querySelector('.mrd-card__finish');
    if (finish && button.getAttribute('data-name')) finish.textContent = button.getAttribute('data-name');

    var price = card.querySelector('.mrd-card__price > span');
    if (price && button.getAttribute('data-price')) {
      price.textContent = button.getAttribute('data-price');
      var prima = button.getAttribute('data-was') || '';
      var was = card.querySelector('.mrd-card__was');
      if (!was && prima) {
        was = document.createElement('s');
        was.className = 'mrd-card__was';
        price.parentNode.appendChild(was);
      }
      if (was) { was.textContent = prima; was.hidden = !prima; }
    }

    var url = button.getAttribute('data-url');
    if (url) {
      Array.prototype.forEach.call(card.querySelectorAll('a.mrd-card__media, .mrd-card__name a'), function (link) {
        link.setAttribute('href', url);
      });
    }

    var id = card.querySelector('[data-mrd-card-id]');
    if (id) id.value = button.getAttribute('data-variant');
    var add = card.querySelector('[data-mrd-card-add]');
    if (add) {
      var disponibile = button.getAttribute('data-available') !== 'false';
      add.disabled = !disponibile;
      add.textContent = add.getAttribute(disponibile ? 'data-label' : 'data-sold-out') || add.textContent;
    }
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[data-mrd-card-pick]');
    if (button) scegliColore(button);
  });

  /* ---------------------------------------------------------------------------
     Avvio, e l'editor di Shopify
     --------------------------------------------------------------------------- */
  var MODULES = [
    { key: 'scroller', selector: '[data-mrd-carousel]', create: createScroller },
    { key: 'fader', selector: '[data-mrd-fade]', create: createFader },
    { key: 'marquee', selector: '[data-mrd-marquee]', create: createMarquee },
    { key: 'videos', selector: '[data-mrd-videos]', create: createVideos },
    { key: 'compare', selector: '[data-mrd-compare]', create: createCompare },
    { key: 'header', selector: '[data-mrd-header]', create: createHeader },
    { key: 'heroVideo', selector: '[data-mrd-hero-video]', create: createHeroVideo },
    { key: 'product', selector: '[data-mrd-product]', create: createProduct },
    { key: 'stickyBar', selector: '[data-mrd-sticky-bar]', create: createStickyBar },
    { key: 'cart', selector: '[data-mrd-cart]', create: createCart },
    { key: 'reviews', selector: '[data-mrd-reviews]', create: createReviews },
    { key: 'zoom', selector: '[data-mrd-zoom]', create: createZoom },
    { key: 'guida', selector: '[data-mrd-guida]', create: createGuida },
    { key: 'bundle', selector: '[data-mrd-bundle]', create: createBundle },
    { key: 'reels', selector: '[data-mrd-reels]', create: createReels },
    { key: 'more', selector: '[data-mrd-more]', create: createMore },
    { key: 'collection', selector: '[data-mrd-collection]', create: createCollection },
    { key: 'footer', selector: '[data-mrd-footer]', create: createFooter },
    { key: 'help', selector: '[data-mrd-help]', create: createHelp }
  ];

  function nodesIn(scope, selector) {
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  /* Pagine comuni: la tab Donna/Uomo accesa e' quella da cui si e' arrivati. Il colore lo da'
     gia' il foglio di stile (data-provenienza, scritto dal layout solo su quelle pagine); qui
     la si dichiara anche a chi legge la pagina ad alta voce. */
  function segnaProvenienza(scope) {
    var html = document.documentElement;
    var da = html.getAttribute('data-provenienza');
    if (da) {
      nodesIn(scope, '[data-mrd-collezione="' + da + '"]').forEach(function (link) {
        link.setAttribute('aria-current', 'true');
      });
    }
    var modo = html.getAttribute('data-collezione');
    var tab = document.querySelector('[data-mrd-collezione="' + modo + '"]');
    if (!tab) return;

    /* «La nostra collezione» ha due pagine, Collezione Uomo e Collezione Donna (sua decisione
       del 04/10/2026). Menu, bottoni e riquadri puntano tutti a /collections: qui ogni link a
       /collections (anche con #ancora) va alla pagina della parte in cui si e'. L'indirizzo
       sta sulle tab (data-mrd-catalogo, scritto in snippets/mrd-tabs.liquid). Senza JavaScript
       resta /collections, che funziona lo stesso. */
    /* In un'altra lingua i link sono «/en/collections» e la home «/en» (RADICE in cima al file). */
    var catalogo = tab.getAttribute('data-mrd-catalogo');
    var tutte = RADICE + 'collections';
    if (catalogo) {
      nodesIn(scope, 'a[href="' + tutte + '"], a[href^="' + tutte + '#"], a[href^="' + tutte + '?"]').forEach(function (link) {
        link.setAttribute('href', catalogo + link.getAttribute('href').slice(tutte.length));
      });
    }

    /* «Devo sempre rimanere su Donna / su Uomo» (sua richiesta del 04/10/2026): logo, voce
       «Home» del menu e del footer portano tutti a «/», che dal 04/10 sera e' la porta
       d'ingresso (video e Donna | Uomo). Chi e' gia' in una delle due parti deve tornare alla
       SUA home, non alla porta: l'indirizzo e' quello della sua tab. Sulla porta d'ingresso
       (data-collezione="scelta") non c'e' una parte, quindi qui non si arriva e «/» resta «/». */
    var casa = tab.getAttribute('href');
    var radici = RADICE === '/' ? ['/'] : [RADICE, RADICE.replace(/\/$/, '')];
    if (!casa || radici.indexOf(casa) !== -1) return;
    nodesIn(scope, radici.map(function (r) { return 'a[href="' + r + '"]'; }).join(', ')).forEach(function (link) {
      if (link.hasAttribute('data-mrd-collezione')) return;
      link.setAttribute('href', casa);
    });
  }

  /* I LINK SCRITTI A MANO NEI TESTI (10/10/2026: l'inglese). Nei testi delle FAQ, delle
     informative, della scheda i link sono scritti col loro indirizzo italiano («/pages/contact»,
     «https://meridianostyle.com/pages/contact»): Shopify non li cambia, e da una pagina inglese
     riporterebbero all'italiano. Qui, fuori dall'italiano, prendono l'inizio della lingua e del
     mercato in cui si e' (RADICE: «/en/», «/en-eu/», «/it-eu/»). Non si toccano i file, la cassa
     e i servizi di Shopify. I link di menu, bottoni e Liquid (routes, page.url) hanno gia' la
     lingua: qui non cambiano. */
  function tieniLingua(scope) {
    if (RADICE === '/') return;
    var radiceSenza = RADICE.replace(/\/$/, '');
    nodesIn(scope, 'a[href]').forEach(function (link) {
      var h = link.getAttribute('href');
      var u;
      try { u = new URL(h, window.location.href); } catch (e) { return; }
      if (u.origin !== window.location.origin) return;
      if (!/^\//.test(h) && h.indexOf(window.location.origin) !== 0) return;   /* #ancora, mailto:, ?tab= */
      var p = u.pathname;
      if (p === radiceSenza || p.indexOf(RADICE) === 0) return;
      if (/^\/(cdn|checkout|checkouts|services|s|wpm|web-pixels|\.well-known|sf_private_access_tokens)(\/|$)/.test(p)) return;
      /* gia' in una lingua o in un mercato (/en/..., /it-eu/...): non si tocca. Le parti degli
         indirizzi di Shopify (pages, products, cart...) sono tutte piu' lunghe di due lettere. */
      if (/^[a-z]{2}(-[a-z0-9]+)?$/i.test(p.split('/')[1] || '')) return;
      link.setAttribute('href', RADICE + p.replace(/^\//, '') + u.search + u.hash);
    });
  }

  function init(scope) {
    shipDates(scope);
    tieniLingua(scope);
    segnaProvenienza(scope);
    MODULES.forEach(function (module) {
      nodesIn(scope, module.selector).forEach(function (node) {
        node.__mrd = node.__mrd || {};
        if (node.__mrd[module.key]) return;
        var controller = module.create(node);
        if (controller) node.__mrd[module.key] = controller;
      });
    });
  }

  function teardown(scope) {
    MODULES.forEach(function (module) {
      nodesIn(scope, module.selector).forEach(function (node) {
        var controller = node.__mrd && node.__mrd[module.key];
        if (!controller) return;
        controller.destroy();
        delete node.__mrd[module.key];
      });
    });
  }

  function controllerFor(element, selector, key) {
    var root = element.closest(selector);
    return root && root.__mrd ? root.__mrd[key] : null;
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });

  /* Selezionando un blocco nell'editor (una slide, una testimonianza) lo si porta in vista. */
  document.addEventListener('shopify:block:select', function (e) {
    var scroller = controllerFor(e.target, '[data-mrd-carousel]', 'scroller');
    if (scroller) scroller.show(e.target);
    var fader = controllerFor(e.target, '[data-mrd-fade]', 'fader');
    if (fader) fader.hold(e.target);
    /* Una categoria o una domanda delle FAQ: si mostra la sua categoria. */
    var help = controllerFor(e.target, '[data-mrd-help]', 'help');
    if (help) help.show(e.target);
  });
  /* Nell'editor, selezionando la sezione del carrello la si vede aperta. */
  document.addEventListener('shopify:section:select', function (e) {
    var cart = controllerFor(e.target, '[data-mrd-cart]', 'cart') ||
      (e.target.querySelector && e.target.querySelector('[data-mrd-cart]') && e.target.querySelector('[data-mrd-cart]').__mrd && e.target.querySelector('[data-mrd-cart]').__mrd.cart);
    if (cart) cart.open();
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var fader = controllerFor(e.target, '[data-mrd-fade]', 'fader');
    if (fader) fader.release();
  });

  window.MrdUI = { init: init, teardown: teardown };

  /* VELOCITA' (13/10/2026): le miniature del carrello che la scheda prodotto carica in anticipo
     (.mrd-pdp__prefetch, in un <template>) entrano in pagina solo a «load», cioe' dopo la foto
     grande: prima partivano a 0,46 s e le rubavano banda (misurato sul sito vero, 4G lento). */
  function precaricaDopo() {
    function metti() {
      Array.prototype.forEach.call(document.querySelectorAll('[data-mrd-prefetch] > template'), function (t) {
        t.parentNode.appendChild(t.content.cloneNode(true));
        t.parentNode.removeChild(t);
      });
    }
    if (document.readyState === 'complete') window.setTimeout(metti, 0);
    else window.addEventListener('load', metti, { once: true });
  }

  bloccaTiraPerAggiornare();
  precaricaDopo();
  unaAllaVolta();
  tieniFermoIlTitolo();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
